import React, { useState, useEffect, useCallback, useRef } from "react";
import bcrypt from "bcryptjs";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Plus,
  Settings,
  Users,
  Calendar as CalendarIcon,
  Clock,
  MapPin,
  Video,
  Filter,
  RefreshCw,
  X,
  Check,
  Edit,
  Trash2,
  MoreVertical,
  Eye,
  EyeOff,
  AlertCircle,
  Phone,
  FileText,
  Play,
  TrendingUp,
  Menu,
  Wrench,
  Ban,
  GripVertical,
  Loader,
  ArrowRight,
  Navigation,
  CheckCircle,
  Mail,
  Award,
  Lock,
} from "lucide-react";
import apiService from "../../services/api";
import { useTheme } from "../../contexts/ThemeContext";
import BookInstallModal from "../BookInstallModal";
import BlockTimeModal from "../BlockTimeModal";
import SendEmailModal from "../SendEmailModal";
import { useRealtimeSync } from "../../hooks/useRealtimeSync";
import EditAppointmentModal from "../EditAppointmentModal";
import NearbySlotSuggesterModal from "../NearbySlotSuggesterModal";

const RecordingPasswordInput = React.memo(
  ({
    recordingPassword,
    setRecordingPassword,
    recordingPasswordError,
    setRecordingPasswordError,
    showRecordingPassword,
    setShowRecordingPassword,
    onUnlock,
    isDarkMode,
  }) => (
    <div className="flex flex-col items-center gap-4 py-6">
      <Lock size={32} className="text-yellow-400" />
      <div
        className={`text-base font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
      >
        This call recording is password protected
      </div>
      <div className="flex flex-col items-center gap-2">
        <div className="flex items-center gap-2">
          <div className="relative">
            <input
              type={showRecordingPassword ? "text" : "password"}
              value={recordingPassword}
              onChange={(e) => {
                setRecordingPassword(e.target.value);
                setRecordingPasswordError(false);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") onUnlock();
              }}
              placeholder="Enter password"
              className={`pl-3 pr-8 py-2 text-sm rounded-lg border outline-none w-40 ${
                recordingPasswordError
                  ? "border-red-500 bg-red-500/10 text-red-400"
                  : isDarkMode
                    ? "bg-[#13161B] border-gray-600 text-white placeholder-gray-500 focus:border-blue-500"
                    : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
              }`}
            />
            <button
              type="button"
              onClick={() => setShowRecordingPassword((v) => !v)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-300"
            >
              {showRecordingPassword ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          </div>
          <button
            onClick={onUnlock}
            className="px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors font-semibold"
          >
            Unlock
          </button>
        </div>
        {recordingPasswordError && (
          <span className="text-xs text-red-400">Incorrect password</span>
        )}
      </div>
    </div>
  ),
);

const ProtectedRecordingBlock = React.memo(
  ({ call, isDarkMode, gateKey, children }) => {
    const [recordingPassword, setRecordingPassword] = useState("");
    const [recordingPasswordError, setRecordingPasswordError] = useState(false);
    const [recordingUnlocked, setRecordingUnlocked] = useState(false);
    const [showRecordingPassword, setShowRecordingPassword] = useState(false);

    useEffect(() => {
      // Reset gate when switching calls
      setRecordingPassword("");
      setRecordingPasswordError(false);
      setRecordingUnlocked(false);
      setShowRecordingPassword(false);
    }, [gateKey]);

    const onUnlock = useCallback(async () => {
      if (!call?.passwordHash) return;
      const match = await bcrypt.compare(recordingPassword, call.passwordHash);
      if (match) {
        setRecordingUnlocked(true);
        setRecordingPasswordError(false);
      } else {
        setRecordingPasswordError(true);
      }
    }, [recordingPassword, call?.passwordHash]);

    if (call?.isPasswordProtected && !recordingUnlocked) {
      return (
        <RecordingPasswordInput
          recordingPassword={recordingPassword}
          setRecordingPassword={setRecordingPassword}
          recordingPasswordError={recordingPasswordError}
          setRecordingPasswordError={setRecordingPasswordError}
          showRecordingPassword={showRecordingPassword}
          setShowRecordingPassword={setShowRecordingPassword}
          onUnlock={onUnlock}
          isDarkMode={isDarkMode}
        />
      );
    }

    return children;
  },
);

const TeamCalendar = () => {
  const { isDarkMode } = useTheme();
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teams, setTeams] = useState([]);
  const [filteredTeams, setFilteredTeams] = useState([]);
  const [userContext, setUserContext] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [calendarSettings, setCalendarSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState("userWeek");
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [includePrivate, setIncludePrivate] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showNewAppointment, setShowNewAppointment] = useState(false);
  const [showBookInstall, setShowBookInstall] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  console.log("hh", selectedAppointment);
  const [error, setError] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [callData, setCallData] = useState([]);
  const [loadingCallData, setLoadingCallData] = useState(false);
  const [selectedCall, setSelectedCall] = useState(null);
  const [lastModifiedActivity, setLastModifiedActivity] = useState(null);
  // const [recordingPassword, setRecordingPassword] = useState("");
  // const [recordingPasswordError, setRecordingPasswordError] = useState(false);
  // const [recordingUnlocked, setRecordingUnlocked] = useState(false);
  // const [showRecordingPassword, setShowRecordingPassword] = useState(false);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [showingOwnOnly, setShowingOwnOnly] = useState(false);
  const [showBlockTime, setShowBlockTime] = useState(false);
  const [showFullTranscript, setShowFullTranscript] = useState(false);
  const [showEditAppointment, setShowEditAppointment] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [showUserSelectionModal, setShowUserSelectionModal] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [userOrder, setUserOrder] = useState([]);
  const [isRestoringTeam, setIsRestoringTeam] = useState(false);
  const [showSendEmail, setShowSendEmail] = useState(false);
  const [emailRecipient, setEmailRecipient] = useState(null);
  const [showNearbySuggester, setShowNearbySuggester] = useState(false);
  const [hideWeekends, setHideWeekends] = useState(
    () => localStorage.getItem("calendarHideWeekends") === "true",
  );
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showQualificationOverride, setShowQualificationOverride] =
    useState(false);
  const [overrideScore, setOverrideScore] = useState("");
  const [updatingScore, setUpdatingScore] = useState(false);

  // Ref for synchronized scrolling
  const scrollContainerRef = useRef(null);

  // Fetch user's team context
  const fetchUserContext = useCallback(async () => {
    try {
      const response = await apiService.getUserTeamContext();
      if (response.success) {
        setUserContext(response.context);
        return response.context;
      }
    } catch (error) {
      console.error("Error fetching user context:", error);
      setError("Failed to load user permissions");
    }
  }, []);
  // NEW: Function to handle qualification score override
  const handleQualificationOverride = async () => {
    if (!selectedAppointment?.id) return;

    const score = parseInt(overrideScore);
    if (isNaN(score) || score < 0 || score > 100) {
      setError("Qualification score must be between 0 and 100");
      return;
    }

    setUpdatingScore(true);
    try {
      const response = await apiService.updateAppointmentQualificationScore(
        selectedAppointment.id,
        score,
      );

      if (response.success) {
        // Update local state
        setSelectedAppointment({
          ...selectedAppointment,
          highestQualificationScore: score,
          metadata: {
            ...selectedAppointment.metadata,
            qualificationScoreOverride: {
              score,
              overriddenAt: new Date().toISOString(),
            },
          },
        });

        // Refresh appointments to update calendar display
        await fetchAppointments();

        setShowQualificationOverride(false);
        setOverrideScore("");
      }
    } catch (error) {
      console.error("Error updating qualification score:", error);
      setError(
        error.response?.data?.message || "Failed to update qualification score",
      );
    } finally {
      setUpdatingScore(false);
    }
  };
  // Filter teams based on permissions
  const filterTeamsByPermissions = (allTeams, context) => {
    if (!context) return [];

    // Show ONLY sales and engineering teams to everyone
    return allTeams.filter((team) => {
      return team.type === "sales" || team.type === "engineering";
    });
  };

  const isEngineeringTeam = () => {
    if (!selectedTeam) return false;
    return selectedTeam.type === "engineering";
  };

  const isSalesTeam = () => {
    if (!selectedTeam) return false;
    return selectedTeam.type === "sales";
  };

  const canViewAllMembers = () => {
    if (!userContext || !selectedTeam) return false;

    const { permissions } = userContext;

    // Check if viewing the engineering calendar
    if (isEngineeringTeam()) {
      return permissions.canViewAllEngineeringMembers;
    }
    // Check if viewing the sales calendar
    else if (isSalesTeam()) {
      return permissions.canViewAllSalesMembers;
    }

    return false;
  };

  // Fetch user's teams - IMPROVED
  const fetchTeams = useCallback(async () => {
    try {
      const context = await fetchUserContext();
      if (!context) return;

      const response = await apiService.getCalendarTeams();
      if (response.success) {
        setTeams(response.teams);
        setFilteredTeams(response.teams);

        // Only auto-select if no saved team exists AND selectedTeam is not already set
        const savedTeamId = localStorage.getItem("selectedTeamId");
        if (response.teams.length > 0 && !selectedTeam && !savedTeamId) {
          let priorityTeam = response.teams.find(
            (t) => t.type === "engineering" || t.type === "operations",
          );

          if (!priorityTeam) {
            priorityTeam = response.teams.find(
              (t) =>
                t.type === "sales" ||
                t.type === "sales_engagement" ||
                t.type === "telemarketing",
            );
          }

          setSelectedTeam(priorityTeam || response.teams[0]);
        }
      }
    } catch (error) {
      console.error("Error fetching teams:", error);
      setError("Failed to load teams");
    }
  }, [selectedTeam, fetchUserContext]);

  // Fetch team calendar settings
  const fetchTeamSettings = useCallback(async () => {
    if (!selectedTeam) return;

    try {
      const response = await apiService.getTeamCalendarSettings(
        selectedTeam.id,
      );
      if (response.success) {
        setCalendarSettings(response.settings);
      }
    } catch (error) {
      console.error("Error fetching team settings:", error);
    }
  }, [selectedTeam]);

  // Fetch team members - IMPROVED
  const fetchTeamMembers = useCallback(async () => {
    if (!selectedTeam || !userContext) return;

    try {
      const response = await apiService.getTeamCalendarMembers(selectedTeam.id);
      if (response.success) {
        setTeamMembers(response.members);

        // Check if we have saved preferences before auto-selecting
        const storageKey = `userOrder_${selectedTeam.id}`;
        const savedData = localStorage.getItem(storageKey);

        // Only auto-select if no saved data exists
        if (!savedData) {
          if (canViewAllMembers()) {
            setSelectedMembers(response.members.map((m) => m.userId));
          } else {
            const currentUserMember = response.members.find(
              (m) => m.userId === userContext.userId,
            );
            if (currentUserMember) {
              setSelectedMembers([currentUserMember.userId]);
            }
          }
        }
        // If saved data exists, let the useEffect handle it
      }
    } catch (error) {
      console.error("Error fetching team members:", error);
    }
  }, [selectedTeam, userContext]);

  // Fetch team appointments
  const fetchAppointments = useCallback(async () => {
    if (!selectedTeam) return;

    try {
      setLoading(true);
      setError(null);

      const { startDate, endDate } = getDateRange();

      const params = {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        includePrivate: includePrivate.toString(),
      };

      if (
        selectedMembers.length > 0 &&
        selectedMembers.length < teamMembers.length &&
        canViewAllMembers()
      ) {
        params.memberIds = selectedMembers;
      }

      const response = await apiService.getTeamAppointments(
        selectedTeam.id,
        params,
      );

      if (response.success) {
        setAppointments(response.appointments);
        setShowingOwnOnly(response.showingOwnOnly || false);
      }
    } catch (error) {
      console.error("Error fetching appointments:", error);
      setError("Failed to load appointments");
    } finally {
      setLoading(false);
    }
  }, [
    selectedTeam,
    selectedDate,
    viewMode,
    selectedMembers,
    teamMembers,
    includePrivate,
  ]);

  // Fetch call data for appointment
  const fetchCallData = async (appointmentId) => {
    try {
      setLoadingCallData(true);
      const response = await apiService.getAppointmentCalls(appointmentId);
      if (response.success) {
        // Deduplicate by callSystemCallId — keep only the first occurrence of each
        const seen = new Set();
        const uniqueCalls = (response.calls || []).filter((call) => {
          const key = call.callSystemCallId || call.id;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
        setCallData(uniqueCalls);
        if (uniqueCalls.length > 0) {
          setSelectedCall(uniqueCalls[0]);
        }
      }
    } catch (error) {
      console.error("Error fetching call data:", error);
      setCallData([]);
    } finally {
      setLoadingCallData(false);
    }
  };

  // Get border color based on qualification score
  const getQualificationColor = (appointment) => {
    if (appointment.type === "other") {
      return null;
    }

    const qualificationScore = appointment.highestQualificationScore;

    if (qualificationScore == null) {
      return "#6B7280"; // Neutral grey — no score yet
    }

    if (qualificationScore >= 75) {
      return "#006400"; // Dark green
    } else if (qualificationScore >= 60) {
      return "#FCF55F"; // Yellow
    } else if (qualificationScore >= 50) {
      return "#FFA500"; // Orange (new tier)
    } else {
      return "#FF0000"; // Red
    }
  };

  // Get date range based on view mode
  const getDateRange = () => {
    const start = new Date(selectedDate);
    const end = new Date(selectedDate);

    switch (viewMode) {
      case "day":
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);
        break;
      case "userWeek":
        const dayOfWeek = start.getDay();
        start.setDate(start.getDate() - dayOfWeek);
        start.setHours(0, 0, 0, 0);
        end.setDate(start.getDate() + 6);
        end.setHours(23, 59, 59, 999);
        break;
      case "month":
        start.setDate(1);
        start.setHours(0, 0, 0, 0);
        end.setMonth(end.getMonth() + 1);
        end.setDate(0);
        end.setHours(23, 59, 59, 999);
        break;
    }

    return { startDate: start, endDate: end };
  };

  const handleRealtimeUpdate = useCallback(
    (data) => {
      console.log("📡 Received real-time appointment update:", data);

      if (data.type === "appointment_updated") {
        const { action, data: appointmentData } = data;

        setAppointments((prevAppointments) => {
          if (action === "created" || action === "time_blocked") {
            const aptStart = new Date(appointmentData.startTime);
            const { startDate, endDate } = getDateRange();

            if (aptStart >= startDate && aptStart <= endDate) {
              const exists = prevAppointments.some(
                (apt) => apt.id === appointmentData.id,
              );
              if (!exists) {
                return [...prevAppointments, appointmentData];
              }
            }
            return prevAppointments;
          } else if (
            action === "updated" ||
            action === "status_changed" ||
            action === "assigned"
          ) {
            return prevAppointments.map((apt) =>
              apt.id === appointmentData.id
                ? { ...apt, ...appointmentData }
                : apt,
            );
          } else if (action === "deleted") {
            return prevAppointments.filter(
              (apt) => apt.id !== appointmentData.id,
            );
          }
          return prevAppointments;
        });

        if (selectedAppointment?.id === appointmentData.id) {
          if (action === "deleted") {
            setSelectedAppointment(null);
          } else {
            setSelectedAppointment((prev) => ({ ...prev, ...appointmentData }));
          }
        }

        if (
          action === "assigned" &&
          appointmentData.assignedTo === userContext?.userId
        ) {
          console.log(
            `📅 Appointment "${appointmentData.title}" was assigned to you`,
          );
        }

        if (action === "time_blocked") {
          console.log(`🚫 Time block created: ${appointmentData.title}`);
        }
      }
    },
    [selectedAppointment, userContext, getDateRange],
  );

  const { isConnected } = useRealtimeSync({
    events: ["appointment_updated"],
    onUpdate: handleRealtimeUpdate,
    enabled: true,
  });

  // Sync team calendar
  const handleSyncCalendar = async () => {
    if (!selectedTeam) return;

    try {
      setSyncing(true);
      const response = await apiService.syncTeamCalendar(selectedTeam.id);
      if (response.success) {
        await fetchAppointments();
        await fetchTeamMembers();
      }
    } catch (error) {
      console.error("Error syncing calendar:", error);
      setError("Failed to sync calendar");
    } finally {
      setSyncing(false);
    }
  };

  // Navigate date
  const navigateDate = (direction) => {
    const newDate = new Date(selectedDate);

    switch (viewMode) {
      case "day":
        newDate.setDate(newDate.getDate() + direction);
        break;
      case "userWeek":
        newDate.setDate(newDate.getDate() + direction * 7);
        break;
      case "month":
        newDate.setMonth(newDate.getMonth() + direction);
        break;
    }

    setSelectedDate(newDate);
  };

  // Toggle member filter
  const toggleMemberFilter = (userId) => {
    if (!canViewAllMembers()) return;

    setSelectedMembers((prev) => {
      if (prev.includes(userId)) {
        return prev.filter((id) => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  // Select all members
  const selectAllMembers = () => {
    if (!canViewAllMembers()) return;
    setSelectedMembers(teamMembers.map((m) => m.userId));
  };

  // Deselect all members
  const deselectAllMembers = () => {
    if (!canViewAllMembers()) return;
    const currentUserMember = teamMembers.find(
      (m) => m.userId === userContext?.userId,
    );
    if (currentUserMember) {
      setSelectedMembers([currentUserMember.userId]);
    } else {
      setSelectedMembers([]);
    }
  };

  // Get member color
  const getMemberColor = (userId) => {
    const member = teamMembers.find((m) => m.userId === userId);
    return member?.color || "#3B82F6";
  };

  // Delete appointment
  const handleDeleteAppointment = async (appointmentId) => {
    try {
      const response = await apiService.deleteAppointment(appointmentId);
      if (response.success) {
        await fetchAppointments();
        setSelectedAppointment(null);
      }
    } catch (error) {
      console.error("Error deleting appointment:", error);
      setError("Failed to delete appointment");
    }
  };

  // Format date display
  const formatDateDisplay = () => {
    const { startDate, endDate } = getDateRange();

    switch (viewMode) {
      case "day":
        return selectedDate.toLocaleDateString("en-US", {
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
        });
      case "userWeek":
        if (startDate.getMonth() === endDate.getMonth()) {
          return `${startDate.toLocaleDateString("en-US", {
            month: "long",
            day: "numeric",
          })} - ${endDate.toLocaleDateString("en-US", {
            day: "numeric",
          })}, ${endDate.getFullYear()}`;
        } else {
          return `${startDate.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          })} - ${endDate.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          })}, ${endDate.getFullYear()}`;
        }
      case "month":
        return selectedDate.toLocaleDateString("en-US", {
          month: "long",
          year: "numeric",
        });
    }
  };

  // Get a display-corrected Date for an appointment's time field.
  // The API returns times with a Z (UTC) suffix but the hour values are
  // already the intended local wall-clock hours (e.g. a 4 PM appointment
  // is stored as "T16:00:00.000Z"). JavaScript's Date constructor converts
  // Z-suffixed strings to local time, adding the BST offset (+1h) and
  // making everything show 1 hour late. Fix: always read the UTC getters
  // so we get the stored hour value as-is.
  const getAptDisplayDate = (apt, field = "startTime") => {
    const raw = new Date(apt[field]);
    return new Date(
      raw.getUTCFullYear(),
      raw.getUTCMonth(),
      raw.getUTCDate(),
      raw.getUTCHours(),
      raw.getUTCMinutes(),
      raw.getUTCSeconds(),
    );
  };

  // Format time — timezone-aware
  const formatTime = (dateString, apt = null) => {
    const date = apt
      ? getAptDisplayDate(
          apt,
          dateString === apt.startTime ? "startTime" : "endTime",
        )
      : new Date(dateString);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Convenience: format start time of an appointment correctly
  const formatAptTime = (apt, field = "startTime") => {
    const date = getAptDisplayDate(apt, field);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Format date
  const formatDate = (date) => {
    return date.toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  // Time slots for calendar
  const timeSlots = Array.from({ length: 34 }, (_, i) => {
    const totalMinutes = 6 * 60 + i * 30;
    const hour = Math.floor(totalMinutes / 60);
    const minute = totalMinutes % 60;
    return { hour, minute };
  });

  // Check if two appointments overlap
  const appointmentsOverlap = (apt1, apt2) => {
    const start1 = new Date(apt1.startTime).getTime();
    const end1 = new Date(apt1.endTime).getTime();
    const start2 = new Date(apt2.startTime).getTime();
    const end2 = new Date(apt2.endTime).getTime();

    return start1 < end2 && start2 < end1;
  };

  // Calculate appointment layout for overlapping events
  const calculateAppointmentLayout = (appointmentsForPeriod) => {
    if (appointmentsForPeriod.length === 0) return [];

    const sorted = [...appointmentsForPeriod].sort((a, b) => {
      const startDiff = new Date(a.startTime) - new Date(b.startTime);
      if (startDiff !== 0) return startDiff;

      const durationA = new Date(a.endTime) - new Date(a.startTime);
      const durationB = new Date(b.endTime) - new Date(b.startTime);
      return durationB - durationA;
    });

    const columns = [];
    const appointmentLayouts = [];

    sorted.forEach((apt) => {
      let columnIndex = 0;
      let placed = false;

      while (!placed) {
        if (!columns[columnIndex]) {
          columns[columnIndex] = [];
        }

        const overlaps = columns[columnIndex].some((existingApt) =>
          appointmentsOverlap(apt, existingApt),
        );

        if (!overlaps) {
          columns[columnIndex].push(apt);
          placed = true;
        } else {
          columnIndex++;
        }
      }

      appointmentLayouts.push({
        appointment: apt,
        column: columnIndex,
      });
    });

    return appointmentLayouts.map((layout) => {
      const { appointment, column } = layout;

      const overlappingColumns = new Set();
      sorted.forEach((otherApt) => {
        if (appointmentsOverlap(appointment, otherApt)) {
          const otherLayout = appointmentLayouts.find(
            (l) => l.appointment.id === otherApt.id,
          );
          if (otherLayout) {
            overlappingColumns.add(otherLayout.column);
          }
        }
      });

      const totalColumns = overlappingColumns.size;
      const width = totalColumns > 0 ? 100 / totalColumns : 100;
      const left = column * width;

      return {
        ...appointment,
        layoutWidth: width,
        layoutLeft: left,
        layoutColumn: column,
        layoutTotalColumns: totalColumns,
      };
    });
  };

  // Get appointments for a specific time slot with layout
  const getAppointmentsForSlot = (date, hour, minute) => {
    const slotAppointments = appointments.filter((apt) => {
      const aptStart = getAptDisplayDate(apt, "startTime");
      return (
        aptStart.getFullYear() === date.getFullYear() &&
        aptStart.getMonth() === date.getMonth() &&
        aptStart.getDate() === date.getDate() &&
        aptStart.getHours() === hour &&
        aptStart.getMinutes() === minute
      );
    });

    return calculateAppointmentLayout(slotAppointments);
  };

  // Get all appointments for a day
  const getAppointmentsForDayWithLayout = (date) => {
    const dayAppointments = appointments.filter((apt) => {
      const aptStart = getAptDisplayDate(apt, "startTime");
      const aptEnd = getAptDisplayDate(apt, "endTime");
      const dayStart = new Date(date);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(date);
      dayEnd.setHours(23, 59, 59, 999);

      return (
        (aptStart >= dayStart && aptStart <= dayEnd) ||
        (aptEnd >= dayStart && aptEnd <= dayEnd) ||
        (aptStart <= dayStart && aptEnd >= dayEnd)
      );
    });

    return calculateAppointmentLayout(dayAppointments);
  };

  // Get appointments for a specific day
  const getAppointmentsForDay = (date) => {
    return appointments.filter((apt) => {
      const aptStart = getAptDisplayDate(apt, "startTime");
      return (
        aptStart.getFullYear() === date.getFullYear() &&
        aptStart.getMonth() === date.getMonth() &&
        aptStart.getDate() === date.getDate()
      );
    });
  };

  // Calculate appointment height based on duration
  const calculateAppointmentHeight = (appointment) => {
    const start = new Date(appointment.startTime);
    const end = new Date(appointment.endTime);
    const duration = (end - start) / (1000 * 60);
    const slots = Math.ceil(duration / 30);
    return slots * 60;
  };

  // Get appointment color
  const getAppointmentColor = (appointment) => {
    if (appointment.isPrivate || appointment.visibility === "private") {
      return "#6B7280";
    }
    return getMemberColor(appointment.assignedTo);
  };

  // Get appointments for user and day
  const getAppointmentsForUserDay = (userId, date) => {
    const dayAppointments = appointments.filter((apt) => {
      const aptStart = getAptDisplayDate(apt, "startTime");
      const aptEnd = getAptDisplayDate(apt, "endTime");
      const dayStart = new Date(date);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(date);
      dayEnd.setHours(23, 59, 59, 999);

      return (
        apt.assignedTo === userId &&
        ((aptStart >= dayStart && aptStart <= dayEnd) ||
          (aptEnd >= dayStart && aptEnd <= dayEnd) ||
          (aptStart <= dayStart && aptEnd >= dayEnd))
      );
    });

    return dayAppointments.sort((a, b) => {
      return new Date(a.startTime) - new Date(b.startTime);
    });
  };

  // Effects
  useEffect(() => {
    fetchTeams();
  }, []);

  useEffect(() => {
    if (selectedTeam && userContext) {
      fetchTeamSettings();
      fetchTeamMembers();
    }
  }, [selectedTeam, userContext]);

  // Fetch team appointments - IMPROVED
  useEffect(() => {
    // Don't fetch if we're still restoring the saved team
    if (isRestoringTeam) {
      return;
    }

    const savedTeamId = localStorage.getItem("selectedTeamId");
    if (savedTeamId && selectedTeam?.id !== savedTeamId) {
      return; // Wait for team restoration to complete
    }

    if (selectedTeam && teamMembers.length > 0) {
      fetchAppointments();
    }
  }, [
    selectedTeam,
    selectedDate,
    viewMode,
    selectedMembers,
    includePrivate,
    teamMembers.length,
    fetchAppointments,
    isRestoringTeam,
  ]);

  useEffect(() => {
    if (selectedAppointment?.id) {
      fetchCallData(selectedAppointment.id);
      // Fetch last modifier from activity log
      apiService
        .getAppointmentActivity(selectedAppointment.id)
        .then((res) => {
          if (res?.success && res.data?.length > 0) {
            setLastModifiedActivity(res.data[0]); // already sorted DESC
          } else {
            setLastModifiedActivity(null);
          }
        })
        .catch(() => setLastModifiedActivity(null));
    } else {
      setCallData([]);
      setSelectedCall(null);
      setLastModifiedActivity(null);
    }
  }, [selectedAppointment?.id]);

  // Load saved team selection from localStorage on mount - IMPROVED
  useEffect(() => {
    const savedTeamId = localStorage.getItem("selectedTeamId");
    if (savedTeamId && teams.length > 0 && !selectedTeam) {
      setIsRestoringTeam(true);
      const savedTeam = teams.find((t) => t.id === savedTeamId);
      if (savedTeam) {
        setSelectedTeam(savedTeam);
        // Small delay to ensure state is updated
        setTimeout(() => setIsRestoringTeam(false), 100);
      } else {
        setIsRestoringTeam(false);
      }
    } else if (!savedTeamId) {
      setIsRestoringTeam(false);
    }
  }, [teams, selectedTeam]);

  // Save team selection to localStorage whenever it changes
  useEffect(() => {
    if (selectedTeam?.id) {
      localStorage.setItem("selectedTeamId", selectedTeam.id);
    }
  }, [selectedTeam]);

  // Load saved user order and selection from localStorage for the current team - IMPROVED
  useEffect(() => {
    if (selectedTeam?.id && teamMembers.length > 0) {
      const storageKey = `userOrder_${selectedTeam.id}`;
      const savedData = localStorage.getItem(storageKey);

      if (savedData) {
        try {
          const { selectedUserIds, orderedUserIds } = JSON.parse(savedData);

          // Verify all user IDs still exist in current team members
          const currentMemberIds = teamMembers.map((m) => m.userId);
          const validSelectedIds = selectedUserIds.filter((id) =>
            currentMemberIds.includes(id),
          );
          const validOrderedIds = orderedUserIds.filter((id) =>
            currentMemberIds.includes(id),
          );

          // Add any new members that aren't in the saved order
          const missingIds = currentMemberIds.filter(
            (id) => !validOrderedIds.includes(id),
          );
          const finalOrderedIds = [...validOrderedIds, ...missingIds];

          // Always set the order
          setUserOrder(finalOrderedIds);

          // Set selected members - use saved if valid, otherwise select all
          if (validSelectedIds.length > 0) {
            setSelectedMembers(validSelectedIds);
          } else {
            setSelectedMembers(currentMemberIds);
          }
        } catch (e) {
          console.error("Error loading saved user preferences:", e);
          // Fallback to default order
          const memberIds = teamMembers.map((m) => m.userId);
          setUserOrder(memberIds);
          setSelectedMembers(memberIds);
        }
      } else {
        // Initialize with current team members
        const memberIds = teamMembers.map((m) => m.userId);
        setUserOrder(memberIds);

        // Only initialize selectedMembers if empty (not already set by fetchTeamMembers)
        if (selectedMembers.length === 0) {
          setSelectedMembers(memberIds);
        }
      }
    }
  }, [selectedTeam?.id, teamMembers.length]);

  // Save user order and selection to localStorage whenever they change
  useEffect(() => {
    if (
      selectedTeam?.id &&
      userOrder.length > 0 &&
      selectedMembers.length > 0
    ) {
      const storageKey = `userOrder_${selectedTeam.id}`;
      const dataToSave = {
        selectedUserIds: selectedMembers,
        orderedUserIds: userOrder,
      };
      localStorage.setItem(storageKey, JSON.stringify(dataToSave));
    }
  }, [selectedTeam?.id, selectedMembers, userOrder]);
  const QualificationOverrideModal = useCallback(() => {
    const currentScore = selectedAppointment?.highestQualificationScore;
    const hasOverride =
      selectedAppointment?.metadata?.qualificationScoreOverride;

    return (
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div
          className={`rounded-xl border max-w-md w-full shadow-2xl ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
        >
          <div
            className={`p-6 border-b flex items-center justify-between ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                <Award size={20} className="text-yellow-400" />
              </div>
              <div>
                <h2
                  className={`text-xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  Override Qualification Score
                </h2>
                <p
                  className={`text-sm mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  Manager override for appointment qualification
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                setShowQualificationOverride(false);
                setOverrideScore("");
              }}
              className={`transition-colors ${isDarkMode ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-900"}`}
            >
              <X size={24} />
            </button>
          </div>

          <div className="p-6 space-y-4">
            <div>
              <label
                className={`block text-sm font-semibold mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
              >
                Current Score
              </label>
              <div
                className={`border rounded-lg p-4 ${isDarkMode ? "bg-[#0C0E12] border-gray-700" : "bg-gray-50 border-gray-200"}`}
              >
                <div
                  className={`text-3xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  {currentScore !== null && currentScore !== undefined
                    ? currentScore
                    : "N/A"}
                </div>
              </div>
            </div>

            <div>
              <label
                className={`block text-sm font-semibold mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
              >
                New Score (0-100) *
              </label>
              <input
                type="number"
                min="0"
                max="100"
                value={overrideScore}
                onChange={(e) => setOverrideScore(e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg focus:border-blue-500 focus:outline-none transition-colors ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                placeholder="Enter score (0-100)"
                autoFocus
              />
            </div>

            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <AlertCircle
                  size={16}
                  className="text-blue-400 mt-0.5 flex-shrink-0"
                />
                <div className="text-sm text-blue-300">
                  <p className="font-semibold mb-1">Manager Override</p>
                  <p className="text-xs text-blue-400/80">
                    This will override the automatic qualification score
                    calculated from call data. The override will be tracked in
                    appointment metadata.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div
            className={`p-6 border-t flex gap-3 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
          >
            <button
              onClick={() => {
                setShowQualificationOverride(false);
                setOverrideScore("");
              }}
              className={`flex-1 px-4 py-3 border rounded-lg font-medium transition-colors ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-gray-300 hover:bg-gray-800" : "bg-gray-50 border-gray-300 text-gray-600 hover:bg-gray-100"}`}
            >
              Cancel
            </button>
            <button
              onClick={handleQualificationOverride}
              disabled={updatingScore || !overrideScore}
              className="flex-1 px-4 py-3 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg font-semibold transition-all duration-200 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {updatingScore ? (
                <>
                  <Loader className="animate-spin" size={18} />
                  <span>Updating...</span>
                </>
              ) : (
                <>
                  <Check size={18} />
                  <span>Override Score</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }, [
    selectedAppointment,
    overrideScore,
    updatingScore,
    setShowQualificationOverride,
    setOverrideScore,
    handleQualificationOverride,
  ]);
  // Appointment Block Component
  const AppointmentBlock = ({
    appointment,
    height,
    onClick,
    layoutWidth,
    layoutLeft,
  }) => {
    const isPrivate =
      appointment.isPrivate || appointment.visibility === "private";
    const color = getAppointmentColor(appointment);
    const member = teamMembers.find((m) => m.userId === appointment.assignedTo);

    const qualificationColor = getQualificationColor(appointment);
    const borderColor = qualificationColor || color;

    const backgroundColor = isSalesTeam() ? "#1e293b" : color;
    const hasScore = appointment.highestQualificationScore != null;
    const titleColor =
      isSalesTeam() && qualificationColor ? qualificationColor : "white";

    return (
      <div
        className={`absolute rounded-lg transition-all border-l-4 overflow-hidden shadow-lg ${
          isPrivate
            ? "opacity-60"
            : "cursor-pointer hover:opacity-90 hover:shadow-xl hover:z-10"
        }`}
        style={{
          backgroundColor: backgroundColor,
          borderLeftColor: borderColor,
          height: height ? `${Math.max(height - 4, 46)}px` : "auto",
          minHeight: "46px",
          width: layoutWidth ? `${layoutWidth}%` : "calc(100% - 8px)",
          left: layoutLeft !== undefined ? `${layoutLeft}%` : "4px",
          right: layoutLeft !== undefined ? "auto" : "4px",
        }}
        onClick={() => !isPrivate && onClick && onClick()}
      >
        <div className="p-2 h-full flex flex-col">
          <div className="flex items-start gap-2 mb-1">
            <div className="flex-1 min-w-0">
              <div
                className="font-semibold text-sm truncate flex items-center gap-1"
                style={{ color: titleColor }}
              >
                {isPrivate && <EyeOff size={12} />}
                {appointment.title}
                {appointment.qualificationScore && (
                  <span className="ml-1 text-xs bg-white/20 px-1.5 py-0.5 rounded">
                    {appointment.qualificationScore}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="text-white/80 text-xs flex items-center gap-1 mb-1">
            <Clock size={10} />
            {formatAptTime(appointment)}
          </div>

          {!isPrivate && member && (
            <div className="flex items-center gap-1 mt-auto">
              <div
                className="w-5 h-5 rounded-full flex items-center justify-center text-white text-xs font-semibold border-2"
                style={{
                  backgroundColor: member.color,
                  borderColor: "rgba(255,255,255,0.3)",
                }}
              >
                {member.user.name.charAt(0).toUpperCase()}
              </div>
              <span className="text-white/70 text-xs truncate">
                {member.user.name}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Compact Appointment Block Component for User Week View
  const CompactAppointmentBlock = ({ appointment, onClick }) => {
    const isPrivate =
      appointment.isPrivate || appointment.visibility === "private";
    const color = getAppointmentColor(appointment);
    const member = teamMembers.find((m) => m.userId === appointment.assignedTo);

    const qualificationColor = getQualificationColor(appointment);
    const borderColor = qualificationColor || color;

    const backgroundColor = isSalesTeam()
      ? isDarkMode
        ? `#1e293b`
        : `#f1f5f9`
      : `${color}15`;
    const hasScore = appointment.highestQualificationScore != null;
    const rawTitleColor =
      isSalesTeam() && qualificationColor ? qualificationColor : color;
    // In light mode, #FCF55F (bright yellow) is illegible — darken to a deep yellow that's still clearly yellow
    const titleColor =
      !isDarkMode && rawTitleColor === "#FCF55F" ? "#c8a800" : rawTitleColor;

    return (
      <div
        className={`rounded-lg border-l-4 overflow-hidden shadow-md transition-all ${
          isPrivate
            ? "opacity-60"
            : "cursor-pointer hover:opacity-90 hover:shadow-lg hover:scale-[1.01]"
        }`}
        style={{
          backgroundColor: backgroundColor,
          borderLeftColor: borderColor,
        }}
        onClick={() => !isPrivate && onClick && onClick()}
      >
        <div className="p-2.5">
          <div className="flex items-center gap-2 mb-2">
            {isPrivate && (
              <EyeOff size={12} className="text-gray-400 flex-shrink-0" />
            )}
            <div
              className={`flex items-center gap-1.5 text-xs font-semibold flex-shrink-0 ${isDarkMode ? "text-white" : "text-gray-700"}`}
            >
              <Clock size={12} />
              <span>{formatAptTime(appointment)}</span>
            </div>
            {appointment.qualificationScore && (
              <span
                className="ml-auto text-xs font-bold px-1.5 py-0.5 rounded"
                style={{
                  backgroundColor: borderColor,
                  color: "white",
                }}
              >
                {appointment.qualificationScore}
              </span>
            )}
          </div>

          {!isPrivate && (
            <>
              {appointment.title && (
                <div
                  className="font-bold text-[10px] leading-tight mb-1.5 break-words"
                  style={{ color: titleColor }}
                >
                  {appointment.title}
                </div>
              )}
            </>
          )}

          {isPrivate && (
            <div className="text-xs text-gray-500 italic">
              Private appointment
            </div>
          )}
        </div>
      </div>
    );
  };

  // Day View Component
  const renderDayView = () => {
    const dayAppointmentsWithLayout =
      getAppointmentsForDayWithLayout(selectedDate);

    return (
      <div
        className={`flex-1 overflow-auto ${isDarkMode ? "bg-[#0A0B0F]" : "bg-white"}`}
      >
        <div className="min-w-full">
          <div className="relative">
            {timeSlots.map((slot, slotIndex) => {
              const slotAppointments = dayAppointmentsWithLayout.filter(
                (apt) => {
                  const aptStart = getAptDisplayDate(apt, "startTime");
                  return (
                    aptStart.getHours() === slot.hour &&
                    aptStart.getMinutes() === slot.minute
                  );
                },
              );

              const timeLabel = `${slot.hour
                .toString()
                .padStart(2, "0")}:${slot.minute.toString().padStart(2, "0")}`;

              return (
                <div
                  key={slotIndex}
                  className={`flex border-b transition-colors ${isDarkMode ? "border-gray-800/50 hover:bg-gray-900/20" : "border-gray-200 hover:bg-gray-50"}`}
                  style={{ minHeight: "60px" }}
                >
                  <div
                    className={`w-16 sm:w-20 flex-shrink-0 p-2 sm:p-3 text-xs font-medium text-right border-r ${isDarkMode ? "text-gray-500 border-gray-800" : "text-gray-400 border-gray-200"}`}
                  >
                    {timeLabel}
                  </div>

                  <div className="flex-1 p-2 relative">
                    {slotAppointments.map((appointment) => (
                      <AppointmentBlock
                        key={appointment.id}
                        appointment={appointment}
                        height={calculateAppointmentHeight(appointment)}
                        onClick={() => setSelectedAppointment(appointment)}
                        layoutWidth={appointment.layoutWidth}
                        layoutLeft={appointment.layoutLeft}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // User Week Horizontal View Component
  const renderUserWeekView = () => {
    const { startDate } = getDateRange();
    const days = Array.from({ length: 7 }, (_, i) => {
      const day = new Date(startDate);
      day.setDate(day.getDate() + i);
      return day;
    }).filter(
      (day) => !hideWeekends || (day.getDay() !== 0 && day.getDay() !== 6),
    );

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const membersToDisplay = userOrder
      .map((userId) => teamMembers.find((m) => m.userId === userId))
      .filter(
        (member) =>
          member &&
          (selectedMembers.length === 0 ||
            selectedMembers.includes(member.userId)),
      );

    const columnWidth = 200;

    const calculateRowHeight = (memberId) => {
      let maxAppointments = 0;
      days.forEach((day) => {
        const dayAppointments = getAppointmentsForUserDay(memberId, day);
        maxAppointments = Math.max(maxAppointments, dayAppointments.length);
      });

      if (maxAppointments === 0) {
        return 140;
      }

      const baseHeight = 100;
      const perAppointmentHeight = 105;

      return baseHeight + maxAppointments * perAppointmentHeight;
    };

    return (
      <div
        className={`flex-1 flex flex-col overflow-hidden ${isDarkMode ? "bg-[#0A0B0F]" : "bg-gray-50"}`}
      >
        <div
          className={`flex-shrink-0 border-b overflow-hidden ${isDarkMode ? "border-gray-800 bg-[#13161B]" : "border-gray-200 bg-white"}`}
        >
          <div className="flex">
            <div
              className={`w-48 flex-shrink-0 border-r p-4 z-10 ${isDarkMode ? "border-gray-800 bg-[#13161B]" : "border-gray-200 bg-white"}`}
            >
              <div
                className={`text-sm font-bold uppercase tracking-wider ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                Team Members
              </div>
            </div>

            <div
              ref={scrollContainerRef}
              className="flex-1 overflow-x-auto overflow-y-hidden scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-gray-900"
              onScroll={(e) => {
                const calendarBody = document.getElementById("calendar-body");
                if (calendarBody) {
                  calendarBody.scrollLeft = e.target.scrollLeft;
                }
              }}
            >
              <div
                className="flex"
                style={{ minWidth: `${columnWidth * days.length}px` }}
              >
                {days.map((day, index) => {
                  const isToday = day.getTime() === today.getTime();
                  return (
                    <div
                      key={index}
                      className={`p-4 text-center border-r last:border-r-0 ${isDarkMode ? "border-gray-800" : "border-gray-200"}`}
                      style={{
                        width: `${columnWidth}px`,
                        minWidth: `${columnWidth}px`,
                      }}
                    >
                      <div
                        className={`text-xs mb-1 font-medium uppercase tracking-wider ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                      >
                        {day.toLocaleDateString("en-US", { weekday: "short" })}
                      </div>
                      <div
                        className={`text-2xl font-bold ${
                          isToday
                            ? "text-blue-400"
                            : isDarkMode
                              ? "text-white"
                              : "text-gray-900"
                        }`}
                      >
                        {day.getDate()}
                      </div>
                      <div
                        className={`text-xs mt-1 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                      >
                        {day.toLocaleDateString("en-US", { month: "short" })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-auto scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-gray-900">
          <div className="flex">
            <div
              className={`w-48 flex-shrink-0 border-r sticky left-0 z-10 ${isDarkMode ? "border-gray-800 bg-[#13161B]" : "border-gray-200 bg-white"}`}
            >
              {membersToDisplay.map((member) => {
                const rowHeight = calculateRowHeight(member.userId);
                return (
                  <div
                    key={member.userId}
                    className={`border-b p-4 flex items-start gap-3 ${isDarkMode ? "border-gray-800/50" : "border-gray-200"}`}
                    style={{ height: `${rowHeight}px` }}
                  >
                    <div
                      className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-base shadow-lg flex-shrink-0"
                      style={{ backgroundColor: member.color }}
                    >
                      {member.user.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div
                        className={`text-sm font-bold truncate mb-1 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        {member.user.name}
                      </div>
                      <div
                        className={`text-xs capitalize ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        {member.role}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div
              id="calendar-body"
              className="flex-1 overflow-x-auto scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-gray-900"
              onScroll={(e) => {
                if (scrollContainerRef.current) {
                  scrollContainerRef.current.scrollLeft = e.target.scrollLeft;
                }
              }}
            >
              <div style={{ minWidth: `${columnWidth * days.length}px` }}>
                {membersToDisplay.map((member) => {
                  const rowHeight = calculateRowHeight(member.userId);
                  return (
                    <div
                      key={member.userId}
                      className={`flex border-b transition-colors ${isDarkMode ? "border-gray-800/50 hover:bg-gray-900/10" : "border-gray-200 hover:bg-gray-50/50"}`}
                      style={{ height: `${rowHeight}px` }}
                    >
                      {days.map((day, dayIndex) => {
                        const isToday = day.getTime() === today.getTime();
                        const dayAppointments = getAppointmentsForUserDay(
                          member.userId,
                          day,
                        );

                        return (
                          <div
                            key={dayIndex}
                            className={`border-r last:border-r-0 p-3 ${isDarkMode ? "border-gray-800/50" : "border-gray-200"} ${
                              isToday
                                ? isDarkMode
                                  ? "bg-blue-900/5"
                                  : "bg-blue-50/30"
                                : ""
                            }`}
                            style={{
                              width: `${columnWidth}px`,
                              minWidth: `${columnWidth}px`,
                            }}
                          >
                            {dayAppointments.length === 0 ? (
                              <div
                                className={`h-full flex items-center justify-center text-xs ${isDarkMode ? "text-white" : "text-gray-400"}`}
                              >
                                No appointments
                              </div>
                            ) : (
                              <div className="space-y-2 h-full">
                                {dayAppointments.map((appointment) => (
                                  <CompactAppointmentBlock
                                    key={appointment.id}
                                    appointment={appointment}
                                    onClick={() =>
                                      setSelectedAppointment(appointment)
                                    }
                                  />
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Month View Component
  const renderMonthView = () => {
    const { startDate } = getDateRange();
    const monthStart = new Date(startDate);
    monthStart.setDate(1);

    const firstDayOfWeek = monthStart.getDay();
    const calendarStart = new Date(monthStart);
    calendarStart.setDate(calendarStart.getDate() - firstDayOfWeek);

    const days = Array.from({ length: 42 }, (_, i) => {
      const day = new Date(calendarStart);
      day.setDate(day.getDate() + i);
      return day;
    });

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return (
      <div
        className={`flex-1 overflow-auto p-2 sm:p-4 ${isDarkMode ? "bg-[#0A0B0F]" : "bg-gray-50"}`}
      >
        <div
          className={`grid grid-cols-7 gap-px rounded-lg overflow-hidden border ${isDarkMode ? "bg-gray-800 border-gray-800" : "bg-gray-200 border-gray-200"}`}
        >
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
            <div
              key={day}
              className={`p-2 sm:p-3 text-center text-xs font-semibold uppercase tracking-wider ${isDarkMode ? "bg-[#13161B] text-gray-400" : "bg-gray-100 text-gray-500"}`}
            >
              <span className="hidden sm:inline">{day}</span>
              <span className="sm:hidden">{day.charAt(0)}</span>
            </div>
          ))}

          {days.map((day, index) => {
            const isCurrentMonth = day.getMonth() === selectedDate.getMonth();
            const isToday = day.getTime() === today.getTime();
            const dayAppointments = getAppointmentsForDay(day);

            return (
              <div
                key={index}
                className={`min-h-[80px] sm:min-h-[120px] p-1 sm:p-2 ${isDarkMode ? "bg-[#13161B]" : "bg-white"} ${
                  !isCurrentMonth ? "opacity-40" : ""
                }`}
              >
                <div className="flex items-center justify-between mb-1 sm:mb-2">
                  <div
                    className={`text-xs sm:text-sm font-semibold ${
                      isToday
                        ? "bg-blue-600 text-white w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center"
                        : isDarkMode
                          ? "text-white"
                          : "text-gray-900"
                    }`}
                  >
                    {day.getDate()}
                  </div>
                  {dayAppointments.length > 0 && (
                    <div
                      className={`text-xs font-medium ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                    >
                      {dayAppointments.length}
                    </div>
                  )}
                </div>

                <div className="space-y-1">
                  {dayAppointments.slice(0, 3).map((appointment) => {
                    const isPrivate =
                      appointment.isPrivate ||
                      appointment.visibility === "private";
                    const color = getAppointmentColor(appointment);
                    const member = teamMembers.find(
                      (m) => m.userId === appointment.assignedTo,
                    );

                    return (
                      <div
                        key={appointment.id}
                        className={`text-xs p-1 sm:p-1.5 rounded border-l-2 ${
                          isPrivate
                            ? "opacity-60"
                            : "cursor-pointer hover:opacity-80"
                        }`}
                        style={{
                          backgroundColor: `${color}20`,
                          borderLeftColor: color,
                        }}
                        onClick={() =>
                          !isPrivate && setSelectedAppointment(appointment)
                        }
                      >
                        <div className="flex items-center gap-1">
                          {isPrivate && (
                            <EyeOff size={10} className="text-gray-400" />
                          )}
                          <span
                            className="font-medium truncate"
                            style={{ color }}
                          >
                            {formatAptTime(appointment)}
                          </span>
                        </div>
                        <div
                          className={`truncate font-medium ${isDarkMode ? "text-white" : "text-gray-800"}`}
                        >
                          {appointment.title}
                        </div>
                        {!isPrivate && member && (
                          <div
                            className={`text-xs truncate hidden sm:block ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                          >
                            {member.user.name}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {dayAppointments.length > 3 && (
                    <div
                      className={`text-xs text-center py-1 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                    >
                      +{dayAppointments.length - 3} more
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Member Filter Sidebar Component
  const MemberFilterSidebar = () => {
    const canViewAll = canViewAllMembers();

    return (
      <div
        className={`w-64 sm:w-72 border-r p-4 sm:p-5 overflow-y-auto ${isDarkMode ? "border-gray-800 bg-[#13161B]" : "border-gray-200 bg-white"}`}
      >
        <div className="mb-6">
          <h3
            className={`text-sm font-bold mb-4 flex items-center gap-2 uppercase tracking-wider ${isDarkMode ? "text-white" : "text-gray-900"}`}
          >
            <Users size={16} className="text-blue-400" />
            Team Members
          </h3>

          {showingOwnOnly && (
            <div className="mb-4 p-3 bg-blue-900/20 border border-blue-800/30 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertCircle
                  size={16}
                  className="text-blue-400 mt-0.5 flex-shrink-0"
                />
                <p className="text-xs text-blue-300">
                  Showing only your appointments. Team members can only view
                  their own calendar.
                </p>
              </div>
            </div>
          )}

          {canViewAll && (
            <div className="flex gap-2 mb-4">
              <button
                onClick={selectAllMembers}
                className={`flex-1 px-3 py-2 text-xs border rounded-lg transition-all font-medium hover:border-blue-500 hover:text-blue-400 ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-gray-300" : "bg-gray-50 border-gray-300 text-gray-600"}`}
              >
                Select All
              </button>
              <button
                onClick={deselectAllMembers}
                className={`flex-1 px-3 py-2 text-xs border rounded-lg transition-all font-medium hover:border-red-500 hover:text-red-400 ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-gray-300" : "bg-gray-50 border-gray-300 text-gray-600"}`}
              >
                Clear
              </button>
            </div>
          )}
        </div>

        <div className="space-y-2 mb-6">
          {teamMembers.map((member) => {
            const isSelected = selectedMembers.includes(member.userId);
            const isDisabled =
              !canViewAll && member.userId !== userContext?.userId;

            return (
              <button
                key={member.userId}
                onClick={() => !isDisabled && toggleMemberFilter(member.userId)}
                disabled={isDisabled}
                className={`w-full flex items-center gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg transition-all ${
                  isSelected
                    ? `border-2 border-blue-500/50 shadow-lg ${isDarkMode ? "bg-[#0C0E12]" : "bg-blue-50"}`
                    : `border ${isDarkMode ? "bg-[#0A0B0F] border-gray-700 hover:border-gray-600" : "bg-gray-50 border-gray-200 hover:border-gray-300"}`
                } ${isDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                <div
                  className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-white font-bold text-xs sm:text-sm shadow-md flex-shrink-0"
                  style={{ backgroundColor: member.color }}
                >
                  {member.user.name.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 text-left min-w-0">
                  <div
                    className={`text-xs sm:text-sm font-semibold truncate ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    {member.user.name}
                  </div>
                  <div
                    className={`text-xs capitalize font-medium ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                  >
                    {member.role}
                  </div>
                </div>
                {!isDisabled && (
                  <div
                    className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all flex-shrink-0 ${
                      isSelected
                        ? "bg-blue-600 border-blue-600"
                        : isDarkMode
                          ? "border-gray-600"
                          : "border-gray-300"
                    }`}
                  >
                    {isSelected && <Check size={14} className="text-white" />}
                  </div>
                )}
              </button>
            );
          })}
        </div>

        <div
          className={`border-t pt-5 ${isDarkMode ? "border-gray-800" : "border-gray-200"}`}
        >
          <h4
            className={`text-xs font-bold mb-3 uppercase tracking-wider ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
          >
            Privacy Settings
          </h4>
          <button
            onClick={() => setIncludePrivate(!includePrivate)}
            className={`w-full flex items-center justify-between gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg transition-all ${
              includePrivate
                ? "bg-blue-900/20 border border-blue-500/50"
                : `border ${isDarkMode ? "bg-[#0A0B0F] border-gray-700 hover:border-gray-600" : "bg-gray-50 border-gray-200 hover:border-gray-300"}`
            }`}
          >
            <div className="flex items-center gap-2 min-w-0">
              {includePrivate ? (
                <Eye size={16} className="text-blue-400 flex-shrink-0" />
              ) : (
                <EyeOff
                  size={16}
                  className={`flex-shrink-0 ${isDarkMode ? "text-gray-400" : "text-gray-400"}`}
                />
              )}
              <div className="text-left min-w-0">
                <div
                  className={`text-xs sm:text-sm font-semibold truncate ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  {includePrivate ? "Showing Private" : "Hiding Private"}
                </div>
                <div
                  className={`text-xs truncate ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                >
                  {includePrivate ? "Your events visible" : "Shown as busy"}
                </div>
              </div>
            </div>
            <div
              className={`w-11 h-6 rounded-full transition-all flex-shrink-0 ${
                includePrivate ? "bg-blue-600" : "bg-gray-600"
              }`}
            >
              <div
                className={`w-5 h-5 bg-white rounded-full m-0.5 transition-transform shadow-md ${
                  includePrivate ? "translate-x-5" : "translate-x-0"
                }`}
              />
            </div>
          </button>
        </div>
      </div>
    );
  };
  // New Appointment Modal
  const NewAppointmentModal = () => {
    const [formData, setFormData] = useState({
      title: "",
      description: "",
      startTime: "",
      endTime: "",
      type: "meeting",
      priority: "medium",
      visibility: "team",
      location: "",
      meetingUrl: "",
      assignedTo: "",
    });
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async (e) => {
      e.preventDefault();

      if (!formData.assignedTo) {
        alert("Please select a team member to assign this appointment to");
        return;
      }

      setSubmitting(true);

      try {
        const response = await apiService.createAppointment(formData);

        if (response.success) {
          setShowNewAppointment(false);
          await fetchAppointments();
        }
      } catch (error) {
        console.error("Error creating appointment:", error);
        setError("Failed to create appointment");
      } finally {
        setSubmitting(false);
      }
    };

    return (
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
        <div
          className={`rounded-xl border max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
        >
          <div
            className={`p-4 sm:p-6 border-b flex items-center justify-between sticky top-0 z-10 ${isDarkMode ? "border-gray-700 bg-[#13161B]" : "border-gray-200 bg-white"}`}
          >
            <h2
              className={`text-lg sm:text-xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
            >
              New Team Appointment
            </h2>
            <button
              onClick={() => setShowNewAppointment(false)}
              className={`transition-colors ${isDarkMode ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-900"}`}
            >
              <X size={24} />
            </button>
          </div>

          <form
            onSubmit={handleSubmit}
            className="p-4 sm:p-6 space-y-4 sm:space-y-5"
          >
            <div>
              <label
                className={`block text-sm font-semibold mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
              >
                Title *
              </label>
              <input
                type="text"
                required
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                className={`w-full px-4 py-3 border rounded-lg focus:border-blue-500 focus:outline-none transition-colors ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                placeholder="Enter appointment title"
              />
            </div>

            <div>
              <label
                className={`block text-sm font-semibold mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
              >
                Assign To *
              </label>
              <select
                required
                value={formData.assignedTo}
                onChange={(e) =>
                  setFormData({ ...formData, assignedTo: e.target.value })
                }
                className={`w-full px-4 py-3 border rounded-lg focus:border-blue-500 focus:outline-none transition-colors ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
              >
                <option value="">Select team member</option>
                {teamMembers.map((member) => (
                  <option key={member.userId} value={member.userId}>
                    {member.user.name} ({member.role})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label
                className={`block text-sm font-semibold mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
              >
                Visibility
              </label>
              <select
                value={formData.visibility}
                onChange={(e) =>
                  setFormData({ ...formData, visibility: e.target.value })
                }
                className={`w-full px-4 py-3 border rounded-lg focus:border-blue-500 focus:outline-none transition-colors ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
              >
                <option value="team">Team - Visible to team members</option>
                <option value="public">
                  Public - Visible to entire company
                </option>
                <option value="private">Private - Only you and assignee</option>
              </select>
            </div>

            <div>
              <label
                className={`block text-sm font-semibold mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
              >
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                className={`w-full px-4 py-3 border rounded-lg focus:border-blue-500 focus:outline-none transition-colors ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                rows={3}
                placeholder="Add description..."
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label
                  className={`block text-sm font-semibold mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                >
                  Start Time *
                </label>
                <input
                  type="datetime-local"
                  required
                  value={formData.startTime}
                  onChange={(e) =>
                    setFormData({ ...formData, startTime: e.target.value })
                  }
                  className={`w-full px-4 py-3 border rounded-lg focus:border-blue-500 focus:outline-none transition-colors ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-semibold mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                >
                  End Time *
                </label>
                <input
                  type="datetime-local"
                  required
                  value={formData.endTime}
                  onChange={(e) =>
                    setFormData({ ...formData, endTime: e.target.value })
                  }
                  className={`w-full px-4 py-3 border rounded-lg focus:border-blue-500 focus:outline-none transition-colors ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label
                  className={`block text-sm font-semibold mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                >
                  Type
                </label>
                <select
                  value={formData.type}
                  onChange={(e) =>
                    setFormData({ ...formData, type: e.target.value })
                  }
                  className={`w-full px-4 py-3 border rounded-lg focus:border-blue-500 focus:outline-none transition-colors ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                >
                  <option value="meeting">Online Meeting</option>
                  <option value="call">Call</option>
                  <option value="site_visit">Site Visit</option>
                  <option value="consultation">Consultation</option>
                  <option value="demo">Demo</option>
                  <option value="follow_up">Follow Up</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label
                  className={`block text-sm font-semibold mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                >
                  Priority
                </label>
                <select
                  value={formData.priority}
                  onChange={(e) =>
                    setFormData({ ...formData, priority: e.target.value })
                  }
                  className={`w-full px-4 py-3 border rounded-lg focus:border-blue-500 focus:outline-none transition-colors ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
            </div>

            <div>
              <label
                className={`block text-sm font-semibold mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
              >
                Location
              </label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) =>
                  setFormData({ ...formData, location: e.target.value })
                }
                className={`w-full px-4 py-3 border rounded-lg focus:border-blue-500 focus:outline-none transition-colors ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                placeholder="Meeting location"
              />
            </div>

            <div>
              <label
                className={`block text-sm font-semibold mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
              >
                Meeting URL
              </label>
              <input
                type="url"
                value={formData.meetingUrl}
                onChange={(e) =>
                  setFormData({ ...formData, meetingUrl: e.target.value })
                }
                className={`w-full px-4 py-3 border rounded-lg focus:border-blue-500 focus:outline-none transition-colors ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                placeholder="https://zoom.us/j/..."
              />
            </div>

            <div
              className={`flex flex-col sm:flex-row gap-3 pt-4 border-t ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
            >
              <button
                type="button"
                onClick={() => setShowNewAppointment(false)}
                className={`flex-1 px-4 py-3 border rounded-lg font-medium transition-colors ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-gray-300 hover:bg-gray-800" : "bg-gray-50 border-gray-300 text-gray-600 hover:bg-gray-100"}`}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 px-4 py-3 text-white rounded-lg font-semibold transition-all duration-300 disabled:opacity-50 hover:shadow-xl"
                style={{
                  background:
                    "radial-gradient(50% 50% at 50% 50%, #427BCA 0%, #2F578F 100%)",
                }}
              >
                {submitting ? "Creating..." : "Create Appointment"}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // Appointment Details Modal
  const renderAppointmentDetailsModal = () => {
    if (!selectedAppointment) return null;

    const color = getAppointmentColor(selectedAppointment);
    const member = teamMembers.find(
      (m) => m.userId === selectedAppointment.assignedTo,
    );
    const call = selectedCall?.callData;

    const isManager = userContext?.roles?.includes("manager");

    // Check if user is in the management team (for installation fields)
    const isInManagementTeam = userContext?.teamTypes?.includes("management");
    const hasQualificationOverride =
      selectedAppointment?.metadata?.qualificationScoreOverride;

    return (
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div
          className={`rounded-xl border max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
        >
          <div
            className={`p-4 sm:p-6 border-b flex items-center justify-between sticky top-0 z-10 ${isDarkMode ? "border-gray-700 bg-[#13161B]" : "border-gray-200 bg-white"}`}
          >
            <div className="flex-1 min-w-0 mr-4">
              <h2
                className={`text-xl sm:text-2xl font-bold mb-1 truncate ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                {selectedAppointment.title}
              </h2>
              {member && (
                <div
                  className={`flex items-center gap-2 text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  <div
                    className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0"
                    style={{ backgroundColor: member.color }}
                  >
                    {member.user.name.charAt(0).toUpperCase()}
                  </div>
                  <span className="truncate">
                    Assigned to {member.user.name}
                  </span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {isManager && (
                <button
                  onClick={() => {
                    const apptContact =
                      selectedAppointment.account?.contacts?.[0];
                    const recipient = {
                      lead: selectedAppointment.lead,
                      opportunity: selectedAppointment.opportunity,
                      account: selectedAppointment.account,
                      fullName:
                        selectedAppointment.lead?.fullName ||
                        (apptContact?.firstName
                          ? `${apptContact.firstName} ${apptContact.lastName || ""}`.trim()
                          : null) ||
                        selectedAppointment.attendees?.[0]?.name ||
                        "Recipient",
                      email:
                        selectedAppointment.lead?.email ||
                        apptContact?.email ||
                        selectedAppointment.account?.email ||
                        selectedAppointment.attendees?.[0]?.email ||
                        "",
                      companyName:
                        selectedAppointment.lead?.companyName ||
                        selectedAppointment.opportunity?.accountName ||
                        selectedAppointment.account?.name ||
                        "",
                      appointmentId: selectedAppointment.id,
                      opportunityId: selectedAppointment.opportunityId,
                      accountId: selectedAppointment.accountId,
                      leadId: selectedAppointment.leadId,
                      appointmentData: {
                        title: selectedAppointment.title,
                        date: formatDate(
                          new Date(selectedAppointment.startTime),
                        ),
                        time: formatAptTime(selectedAppointment),
                        location: selectedAppointment.location || "",
                        description: selectedAppointment.description || "",
                        assignedTo: member?.user?.name || "",
                      },
                    };
                    setEmailRecipient(recipient);
                    setShowSendEmail(true);
                  }}
                  className={`p-2 transition-colors ${isDarkMode ? "text-gray-400 hover:text-blue-400" : "text-gray-500 hover:text-blue-500"}`}
                  title="Send Email (Manager Only)"
                >
                  <Mail size={20} />
                </button>
              )}

              {/* Qualification override button - only show for managers with score */}
              {isManager &&
                selectedAppointment.highestQualificationScore !== null && (
                  <button
                    onClick={() => {
                      setOverrideScore(
                        selectedAppointment.highestQualificationScore?.toString() ||
                          "",
                      );
                      setShowQualificationOverride(true);
                    }}
                    className={`p-2 transition-colors ${isDarkMode ? "text-gray-400 hover:text-yellow-400" : "text-gray-500 hover:text-yellow-500"}`}
                    title="Override Qualification Score (Manager Only)"
                  >
                    <Award size={20} />
                  </button>
                )}
              <button
                onClick={() => {
                  setEditingAppointment(selectedAppointment);
                  setShowEditAppointment(true);
                  setSelectedAppointment(null);
                  setShowFullTranscript(false);
                }}
                className="p-2 text-gray-400 hover:text-white transition-colors"
              >
                <Edit size={20} />
              </button>
              <button
                onClick={() => {
                  if (isManager) {
                    setShowDeleteConfirm(true);
                  }
                }}
                disabled={!isManager}
                className={`p-2 transition-colors ${
                  isManager
                    ? "text-gray-400 hover:text-red-500 cursor-pointer"
                    : "text-gray-600 cursor-not-allowed opacity-50"
                }`}
                title={
                  !isManager
                    ? "Only managers can delete appointments"
                    : "Delete appointment"
                }
              >
                <Trash2 size={20} />
              </button>
              <button
                onClick={() => {
                  setSelectedAppointment(null);
                  setShowFullTranscript(false);
                }}
                className="p-2 text-gray-400 hover:text-white transition-colors"
              >
                <X size={24} />
              </button>
            </div>
          </div>

          <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
            <div className="space-y-4">
              <div className="flex flex-col gap-2">
                {selectedAppointment.opportunity && (
                  <a
                    href={`/opportunities/${selectedAppointment.opportunity.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-lg font-semibold text-blue-400 hover:text-blue-300 transition-colors group"
                  >
                    <FileText
                      size={20}
                      className="group-hover:scale-110 transition-transform"
                    />
                    <span className="underline decoration-blue-400/50 decoration-2 underline-offset-4 group-hover:decoration-blue-300">
                      {selectedAppointment.opportunity.name ||
                        `Opportunity #${selectedAppointment.opportunity.id}`}
                    </span>
                  </a>
                )}

                {selectedAppointment.account && (
                  <a
                    href={`/accounts/${selectedAppointment.account.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-lg font-semibold text-green-400 hover:text-green-300 transition-colors group"
                  >
                    <FileText
                      size={20}
                      className="group-hover:scale-110 transition-transform"
                    />
                    <span className="underline decoration-green-400/50 decoration-2 underline-offset-4 group-hover:decoration-green-300">
                      {selectedAppointment.account.name ||
                        `Account #${selectedAppointment.account.id}`}
                    </span>
                  </a>
                )}

                {selectedAppointment.lead && (
                  <a
                    href={`/leads/${selectedAppointment.lead.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-lg font-semibold text-purple-400 hover:text-purple-300 transition-colors group"
                  >
                    <FileText
                      size={20}
                      className="group-hover:scale-110 transition-transform"
                    />
                    <span className="underline decoration-purple-400/50 decoration-2 underline-offset-4 group-hover:decoration-purple-300">
                      {selectedAppointment.lead.firstName ||
                      selectedAppointment.lead.lastName
                        ? `${selectedAppointment.lead.firstName ?? ""} ${selectedAppointment.lead.lastName ?? ""}`.trim()
                        : selectedAppointment.lead.companyName ||
                          `Lead #${selectedAppointment.lead.id}`}
                    </span>
                  </a>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <span
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white shadow-md"
                  style={{ backgroundColor: color }}
                >
                  {selectedAppointment.status}
                </span>
                <span
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${isDarkMode ? "bg-gray-700 text-gray-200" : "bg-gray-100 text-gray-700"}`}
                >
                  {selectedAppointment.type}
                </span>
                <span
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${isDarkMode ? "bg-gray-700 text-gray-200" : "bg-gray-100 text-gray-700"}`}
                >
                  {selectedAppointment.priority}
                </span>
                <span
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${isDarkMode ? "bg-gray-700 text-gray-200" : "bg-gray-100 text-gray-700"}`}
                >
                  {selectedAppointment.visibility}
                </span>
              </div>

              <div className="flex items-start gap-3">
                <Clock
                  size={20}
                  className="text-blue-400 mt-0.5 flex-shrink-0"
                />
                <div>
                  <div
                    className={`font-semibold text-base sm:text-lg ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    {formatAptTime(selectedAppointment)} -{" "}
                    {formatAptTime(selectedAppointment, "endTime")}
                  </div>
                  <div
                    className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    {formatDate(new Date(selectedAppointment.startTime))}
                  </div>
                </div>
              </div>

              {selectedAppointment.location && (
                <div className="flex items-start gap-3">
                  <MapPin
                    size={20}
                    className="text-green-400 mt-0.5 flex-shrink-0"
                  />
                  <div
                    className={`font-medium break-words ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    {selectedAppointment.location}
                  </div>
                </div>
              )}

              {selectedAppointment.meetingUrl && (
                <div className="flex items-start gap-3">
                  <Video
                    size={20}
                    className="text-purple-400 mt-0.5 flex-shrink-0"
                  />
                  <a
                    href={selectedAppointment.meetingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-400 hover:text-blue-300 transition-colors break-all font-medium"
                  >
                    {selectedAppointment.meetingUrl}
                  </a>
                </div>
              )}

              {selectedAppointment.description && (
                <div
                  className={`border rounded-lg p-4 ${isDarkMode ? "bg-[#0C0E12] border-gray-700" : "bg-gray-50 border-gray-200"}`}
                >
                  <div
                    className={`text-sm font-semibold mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Description
                  </div>
                  <div
                    className={`whitespace-pre-wrap leading-relaxed ${isDarkMode ? "text-white" : "text-gray-800"}`}
                  >
                    {selectedAppointment.description}
                  </div>
                </div>
              )}
              {selectedAppointment.salesType && (
                <div
                  className={`border rounded-lg p-4 ${isDarkMode ? "bg-[#0C0E12] border-gray-700" : "bg-gray-50 border-gray-200"}`}
                >
                  <div
                    className={`text-sm font-semibold mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Appointment Type
                  </div>
                  <div
                    className={`font-medium capitalize ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    {selectedAppointment.salesType.replace("_", " ")}
                  </div>
                </div>
              )}
              {selectedAppointment.highestQualificationScore !== null && (
                <div
                  className={`border rounded-lg p-4 ${isDarkMode ? "bg-[#0C0E12] border-gray-700" : "bg-gray-50 border-gray-200"}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span
                      className={`text-sm font-semibold ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Qualification Score
                    </span>
                    {hasQualificationOverride && (
                      <div className="flex items-center gap-1 text-xs text-yellow-400">
                        <Award size={12} />
                        <span>Overridden</span>
                      </div>
                    )}
                  </div>
                  <div
                    className={`text-3xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    {selectedAppointment.highestQualificationScore}
                  </div>
                  {hasQualificationOverride && (
                    <div
                      className={`mt-2 text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                    >
                      Overridden on{" "}
                      {new Date(
                        hasQualificationOverride.overriddenAt,
                      ).toLocaleString()}
                    </div>
                  )}
                </div>
              )}

              {selectedAppointment.attendees &&
                selectedAppointment.attendees.length > 0 && (
                  <div
                    className={`border rounded-lg p-4 ${isDarkMode ? "bg-[#0C0E12] border-gray-700" : "bg-gray-50 border-gray-200"}`}
                  >
                    <div
                      className={`text-sm font-semibold mb-3 flex items-center gap-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      <Users size={16} />
                      Attendees ({selectedAppointment.attendees.length})
                    </div>
                    <div className="space-y-2">
                      {selectedAppointment.attendees.map((attendee, index) => (
                        <div
                          key={index}
                          className={`flex items-center gap-3 p-3 rounded-lg ${isDarkMode ? "bg-[#13161B]" : "bg-white border border-gray-200"}`}
                        >
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-bold shadow-md flex-shrink-0">
                            {(attendee.name || attendee.email)
                              .charAt(0)
                              .toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div
                              className={`font-semibold truncate ${isDarkMode ? "text-white" : "text-gray-900"}`}
                            >
                              {attendee.name || attendee.email}
                            </div>
                            {attendee.name && (
                              <div
                                className={`text-xs truncate ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                              >
                                {attendee.email}
                              </div>
                            )}
                          </div>
                          {attendee.responseStatus && (
                            <div
                              className={`text-xs px-3 py-1 rounded-full font-semibold flex-shrink-0 ${
                                attendee.responseStatus === "accepted"
                                  ? "bg-green-500/20 text-green-400"
                                  : attendee.responseStatus === "declined"
                                    ? "bg-red-500/20 text-red-400"
                                    : attendee.responseStatus === "tentative"
                                      ? "bg-yellow-500/20 text-yellow-400"
                                      : "bg-gray-500/20 text-gray-400"
                              }`}
                            >
                              {attendee.responseStatus}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
            </div>

            {loadingCallData ? (
              <div
                className={`border rounded-xl p-6 ${isDarkMode ? "bg-[#0C0E12] border-gray-700" : "bg-gray-50 border-gray-200"}`}
              >
                <div
                  className={`text-center ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  Loading call data...
                </div>
              </div>
            ) : callData.length > 0 ? (
              <div
                className={`space-y-4 border-t pt-4 sm:pt-6 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
              >
                <h3
                  className={`text-lg sm:text-xl font-bold flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  <Phone size={20} className="text-green-400" />
                  Call Recordings
                </h3>

                {callData.length > 1 && (
                  <div
                    className={`border rounded-xl p-4 ${isDarkMode ? "bg-[#0C0E12] border-gray-700" : "bg-gray-50 border-gray-200"}`}
                  >
                    <h4
                      className={`text-sm font-semibold mb-3 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Related Calls ({callData.length})
                    </h4>
                    <div className="space-y-2">
                      {callData.map((c) => (
                        <button
                          key={c.id}
                          onClick={() => {
                            setSelectedCall(c);
                          }}
                          className={`w-full text-left p-3 rounded-lg transition-all ${
                            selectedCall?.id === c.id
                              ? "bg-blue-500/10 border-2 border-blue-500/50 shadow-lg"
                              : `border ${isDarkMode ? "bg-[#13161B] border-gray-700 hover:border-gray-600" : "bg-white border-gray-200 hover:border-gray-300"}`
                          }`}
                        >
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                            <div className="flex items-center gap-2">
                              <Phone
                                size={16}
                                className="text-green-400 flex-shrink-0"
                              />
                              <span
                                className={`text-sm font-semibold break-all ${isDarkMode ? "text-white" : "text-gray-900"}`}
                              >
                                {c.phoneNumber}
                              </span>
                            </div>
                            <span
                              className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                            >
                              {new Date(c.callStartTime).toLocaleString()}
                            </span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {call && (
                  <div
                    className={`border rounded-xl p-4 sm:p-6 space-y-4 ${isDarkMode ? "bg-[#0C0E12] border-gray-700" : "bg-gray-50 border-gray-200"}`}
                  >
                    <ProtectedRecordingBlock
                      call={call}
                      isDarkMode={isDarkMode}
                      gateKey={
                        selectedCall?.id ||
                        call.callSystemCallId ||
                        call.recordingUrl
                      }
                    >
                      <>
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                          <h4
                            className={`text-base sm:text-lg font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                          >
                            Call Details
                          </h4>
                          {call.recordingUrl && (
                            <a
                              href={call.recordingUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold shadow-lg"
                            >
                              <Play size={16} />
                              Play Recording
                            </a>
                          )}
                        </div>

                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                          <div
                            className={`border rounded-lg p-3 sm:p-4 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
                          >
                            <div className="flex items-center gap-2 mb-2">
                              <Clock size={14} className="text-blue-400" />
                              <span
                                className={`text-xs font-semibold ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                              >
                                Duration
                              </span>
                            </div>
                            <div
                              className={`text-base sm:text-lg font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                            >
                              {Math.floor(call.duration / 60)}m{" "}
                              {call.duration % 60}s
                            </div>
                          </div>

                          <div
                            className={`border rounded-lg p-3 sm:p-4 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
                          >
                            <div className="flex items-center gap-2 mb-2">
                              <Phone size={14} className="text-green-400" />
                              <span
                                className={`text-xs font-semibold ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                              >
                                Direction
                              </span>
                            </div>
                            <div
                              className={`text-base sm:text-lg font-bold capitalize ${isDarkMode ? "text-white" : "text-gray-900"}`}
                            >
                              {call.direction}
                            </div>
                          </div>

                          {call.qualification && (
                            <>
                              <div
                                className={`border rounded-lg p-3 sm:p-4 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
                              >
                                <div className="flex items-center gap-2 mb-2">
                                  <TrendingUp
                                    size={14}
                                    className="text-purple-400"
                                  />
                                  <span
                                    className={`text-xs font-semibold ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                                  >
                                    Status
                                  </span>
                                </div>
                                <div
                                  className={`text-base sm:text-lg font-bold ${
                                    call.qualification.isQualified
                                      ? "text-green-400"
                                      : "text-red-400"
                                  }`}
                                >
                                  {call.qualification.isQualified
                                    ? "Qualified"
                                    : "Not Qualified"}
                                </div>
                              </div>

                              <div
                                className={`border rounded-lg p-3 sm:p-4 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
                              >
                                <div className="flex items-center gap-2 mb-2">
                                  <FileText
                                    size={14}
                                    className="text-yellow-400"
                                  />
                                  <span
                                    className={`text-xs font-semibold ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                                  >
                                    Score
                                  </span>
                                </div>
                                <div
                                  className={`text-base sm:text-lg font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                                >
                                  {call.qualification.qualificationScore}/100
                                </div>
                              </div>
                            </>
                          )}
                        </div>

                        {call.qualification && (
                          <div
                            className={`border rounded-xl p-4 sm:p-6 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-gray-100 border-gray-200"}`}
                          >
                            <h5
                              className={`text-lg sm:text-xl font-bold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                            >
                              Business Qualification Criteria
                            </h5>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                              <div
                                className={`border rounded-lg p-4 relative ${isDarkMode ? "bg-[#0C0E12] border-gray-700" : "bg-white border-gray-200"}`}
                              >
                                <div className="flex items-start justify-between mb-3">
                                  <div
                                    className={`text-sm font-semibold ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                                  >
                                    Monthly Spend
                                  </div>
                                  <div>
                                    {call.qualification.meetsSpendThreshold ? (
                                      <Check
                                        size={20}
                                        className="text-green-400"
                                      />
                                    ) : (
                                      <X size={20} className="text-red-400" />
                                    )}
                                  </div>
                                </div>
                                <div
                                  className={`text-2xl sm:text-3xl font-bold mb-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                                >
                                  £
                                  {parseFloat(
                                    call.qualification.currentSpend || 0,
                                  ).toFixed(2)}
                                </div>
                                <div className="text-xs text-gray-400">
                                  Threshold: £0+ (
                                  {call.qualification.meetsSpendThreshold ? (
                                    <span className="text-green-400 font-semibold">
                                      Met
                                    </span>
                                  ) : (
                                    <span className="text-red-400 font-semibold">
                                      Not Met
                                    </span>
                                  )}
                                  )
                                </div>
                              </div>

                              <div
                                className={`border rounded-lg p-4 relative ${isDarkMode ? "bg-[#0C0E12] border-gray-700" : "bg-white border-gray-200"}`}
                              >
                                <div className="flex items-start justify-between mb-3">
                                  <div
                                    className={`text-sm font-semibold ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                                  >
                                    System Age
                                  </div>
                                </div>
                                <div
                                  className={`text-2xl sm:text-3xl font-bold mb-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                                >
                                  {call.qualification.phoneSystemAge
                                    ? `${parseFloat(call.qualification.phoneSystemAge).toFixed(2)} years`
                                    : "N/A"}
                                </div>
                              </div>

                              <div
                                className={`border rounded-lg p-4 relative ${isDarkMode ? "bg-[#0C0E12] border-gray-700" : "bg-white border-gray-200"}`}
                              >
                                <div className="flex items-start justify-between mb-3">
                                  <div
                                    className={`text-sm font-semibold ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                                  >
                                    Handsets
                                  </div>
                                  <div>
                                    {call.qualification
                                      .meetsHandsetThreshold ? (
                                      <Check
                                        size={20}
                                        className="text-green-400"
                                      />
                                    ) : (
                                      <X size={20} className="text-red-400" />
                                    )}
                                  </div>
                                </div>
                                <div
                                  className={`text-2xl sm:text-3xl font-bold mb-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                                >
                                  {call.qualification.numberOfHandsets
                                    ? parseFloat(
                                        call.qualification.numberOfHandsets,
                                      ).toFixed(1)
                                    : "N/A"}
                                </div>
                                <div
                                  className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                                >
                                  Threshold: 1+ (
                                  {call.qualification.meetsHandsetThreshold ? (
                                    <span className="text-green-400 font-semibold">
                                      Met
                                    </span>
                                  ) : (
                                    <span className="text-red-400 font-semibold">
                                      Not Met
                                    </span>
                                  )}
                                  )
                                </div>
                              </div>

                              <div
                                className={`border rounded-lg p-4 relative ${isDarkMode ? "bg-[#0C0E12] border-gray-700" : "bg-white border-gray-200"}`}
                              >
                                <div className="flex items-start justify-between mb-3">
                                  <div
                                    className={`text-sm font-semibold ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                                  >
                                    Contract Remaining
                                  </div>
                                  <div>
                                    {call.qualification
                                      .meetsContractThreshold ? (
                                      <Check
                                        size={20}
                                        className="text-green-400"
                                      />
                                    ) : (
                                      <X size={20} className="text-red-400" />
                                    )}
                                  </div>
                                </div>
                                <div
                                  className={`text-2xl sm:text-3xl font-bold mb-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                                >
                                  {call.qualification.contractYearsRemaining
                                    ? `${parseFloat(call.qualification.contractYearsRemaining).toFixed(2)} years`
                                    : "N/A"}
                                </div>
                                <div
                                  className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                                >
                                  Threshold: ≤3.5 years (
                                  {call.qualification.meetsContractThreshold ? (
                                    <span className="text-green-400 font-semibold">
                                      Met
                                    </span>
                                  ) : (
                                    <span className="text-red-400 font-semibold">
                                      Not Met
                                    </span>
                                  )}
                                  )
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {call.insights?.summary && (
                          <div
                            className={`border rounded-lg p-4 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
                          >
                            <h5
                              className={`text-sm font-semibold mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                            >
                              Call Summary
                            </h5>
                            <p
                              className={`text-sm leading-relaxed ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                            >
                              {call.insights.summary}
                            </p>
                          </div>
                        )}

                        {call.transcripts && call.transcripts.length > 0 && (
                          <div
                            className={`border rounded-lg p-4 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
                          >
                            <h5
                              className={`text-sm font-semibold mb-3 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                            >
                              Transcript
                            </h5>
                            <div
                              className={`space-y-3 ${showFullTranscript ? "max-h-[600px]" : "max-h-64"} overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-gray-900`}
                            >
                              {(showFullTranscript
                                ? call.transcripts
                                : call.transcripts.slice(0, 5)
                              ).map((transcript, index) => (
                                <div
                                  key={index}
                                  className={`flex gap-2 sm:gap-3 pb-3 border-b last:border-b-0 ${isDarkMode ? "border-gray-800" : "border-gray-100"}`}
                                >
                                  <div
                                    className={`text-xs min-w-[50px] sm:min-w-[60px] font-mono flex-shrink-0 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                                  >
                                    {Math.floor(transcript.startTime / 60)}:
                                    {String(
                                      Math.floor(transcript.startTime % 60),
                                    ).padStart(2, "0")}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="text-xs font-semibold text-blue-400 mb-1">
                                      {transcript.speakerLabel}:
                                    </div>
                                    <p
                                      className={`text-sm leading-relaxed break-words ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                                    >
                                      {transcript.text}
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                            {call.transcripts.length > 5 && (
                              <div className="text-center mt-4">
                                <button
                                  onClick={() =>
                                    setShowFullTranscript(!showFullTranscript)
                                  }
                                  className="text-sm text-blue-400 hover:text-blue-300 font-semibold transition-colors"
                                >
                                  {showFullTranscript
                                    ? "← Show less"
                                    : "View full transcript →"}
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    </ProtectedRecordingBlock>
                  </div>
                )}
              </div>
            ) : null}

            {selectedAppointment.creator && (
              <div
                className={`pt-4 border-t ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
              >
                <div
                  className={`text-xs break-words ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                >
                  Created by {selectedAppointment.creator.name} on{" "}
                  {new Date(selectedAppointment.createdAt).toLocaleString()}
                </div>
                {lastModifiedActivity?.user && (
                  <div
                    className={`text-xs break-words mt-0.5 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                  >
                    Last modified by {lastModifiedActivity.user.name} on{" "}
                    {new Date(lastModifiedActivity.createdAt).toLocaleString()}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Drag and drop handlers for user ordering
  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    let orderedMembers;
    if (userOrder && userOrder.length > 0) {
      orderedMembers = userOrder
        .map((userId) => teamMembers.find((m) => m.userId === userId))
        .filter(Boolean);

      const orderedIds = orderedMembers.map((m) => m.userId);
      const missingMembers = teamMembers.filter(
        (m) => !orderedIds.includes(m.userId),
      );
      orderedMembers = [...orderedMembers, ...missingMembers];
    } else {
      orderedMembers = [...teamMembers];
    }

    const newOrder = orderedMembers.map((m) => m.userId);
    const draggedUserId = newOrder[draggedIndex];
    newOrder.splice(draggedIndex, 1);
    newOrder.splice(index, 0, draggedUserId);

    setUserOrder(newOrder);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const toggleUserSelection = (userId) => {
    setSelectedMembers((prev) => {
      if (prev.includes(userId)) {
        if (prev.length === 1) return prev;
        return prev.filter((id) => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  // User Selection Modal Component
  const UserSelectionModal = () => {
    if (!teamMembers || teamMembers.length === 0) {
      return null;
    }

    let orderedMembers;
    if (userOrder && userOrder.length > 0) {
      orderedMembers = userOrder
        .map((userId) => teamMembers.find((m) => m.userId === userId))
        .filter(Boolean);

      const orderedIds = orderedMembers.map((m) => m.userId);
      const missingMembers = teamMembers.filter(
        (m) => !orderedIds.includes(m.userId),
      );
      orderedMembers = [...orderedMembers, ...missingMembers];
    } else {
      orderedMembers = [...teamMembers];
    }

    return (
      <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
        <div
          className={`rounded-xl shadow-2xl border w-full max-w-md max-h-[80vh] flex flex-col ${isDarkMode ? "bg-[#1A1D24] border-gray-700" : "bg-white border-gray-200"}`}
        >
          <div
            className={`p-6 border-b ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
          >
            <div className="flex items-center justify-between">
              <div>
                <h2
                  className={`text-xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  Select Team Members
                </h2>
                <p
                  className={`text-sm mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  Choose and reorder calendars for week view
                </p>
              </div>
              <button
                onClick={() => setShowUserSelectionModal(false)}
                className={`p-2 rounded-lg transition-colors ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"}`}
              >
                <X
                  size={20}
                  className={isDarkMode ? "text-gray-400" : "text-gray-500"}
                />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-6 space-y-2">
            {orderedMembers.map((member, index) => {
              const userName = member.user?.name || member.name || "Unknown";
              const userRole = member.role || "member";
              const userColor = member.color || "#666";

              return (
                <div
                  key={member.userId}
                  draggable
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragEnd={handleDragEnd}
                  className={`flex items-center gap-3 p-3 rounded-lg border transition-all cursor-move ${
                    draggedIndex === index
                      ? "bg-blue-500/20 border-blue-500"
                      : `${isDarkMode ? "bg-[#0C0E12] border-gray-700 hover:border-gray-600" : "bg-gray-50 border-gray-200 hover:border-gray-300"}`
                  }`}
                >
                  <GripVertical
                    size={20}
                    className={
                      isDarkMode
                        ? "text-gray-500 flex-shrink-0"
                        : "text-gray-400 flex-shrink-0"
                    }
                  />

                  <button
                    onClick={() => toggleUserSelection(member.userId)}
                    className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                      selectedMembers.includes(member.userId)
                        ? "bg-blue-600 border-blue-600"
                        : isDarkMode
                          ? "border-gray-600 hover:border-gray-500"
                          : "border-gray-300 hover:border-gray-400"
                    }`}
                  >
                    {selectedMembers.includes(member.userId) && (
                      <Check size={14} className="text-white" />
                    )}
                  </button>

                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0"
                      style={{ backgroundColor: userColor }}
                    >
                      {userName.charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div
                        className={`font-medium truncate ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        {userName}
                      </div>
                      <div
                        className={`text-xs truncate capitalize ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        {userRole}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div
            className={`p-6 border-t ${isDarkMode ? "border-gray-700 bg-[#0C0E12]" : "border-gray-200 bg-gray-50"}`}
          >
            <div
              className={`flex items-center justify-between text-sm mb-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            >
              <span>
                {selectedMembers.length} of {teamMembers.length} selected
              </span>
              <button
                onClick={() =>
                  setSelectedMembers(teamMembers.map((m) => m.userId))
                }
                className="text-blue-400 hover:text-blue-300 font-medium"
              >
                Select All
              </button>
            </div>
            <button
              onClick={() => setShowUserSelectionModal(false)}
              className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors"
            >
              Apply Changes
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (filteredTeams.length === 0 && !loading && userContext) {
    return (
      <div
        className={`min-h-screen flex items-center justify-center p-4 ${isDarkMode ? "bg-[#0A0B0F] text-white" : "bg-gray-50 text-gray-900"}`}
      >
        <div className="text-center max-w-md">
          <AlertCircle
            size={64}
            className={`mx-auto mb-4 ${isDarkMode ? "text-white" : "text-gray-400"}`}
          />
          <h2 className="text-2xl font-bold mb-2">No Calendar Access</h2>
          <p className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
            You don't have access to any team calendars. Please contact your
            administrator.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen ${isDarkMode ? "bg-[#0A0B0F] text-white" : "bg-gray-50 text-gray-900"}`}
    >
      <div className="flex min-h-screen">
        {teamMembers.length > 0 &&
          viewMode !== "userWeek" &&
          canViewAllMembers() && (
            <div className="hidden lg:block">
              <MemberFilterSidebar />
            </div>
          )}

        {showMobileSidebar &&
          viewMode !== "userWeek" &&
          canViewAllMembers() && (
            <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 lg:hidden">
              <div
                className={`absolute left-0 top-0 bottom-0 w-full max-w-sm shadow-2xl overflow-y-auto ${isDarkMode ? "bg-[#13161B]" : "bg-white"}`}
              >
                <div
                  className={`p-4 border-b flex items-center justify-between ${isDarkMode ? "border-gray-800" : "border-gray-200"}`}
                >
                  <h3
                    className={`text-lg font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    Filters
                  </h3>
                  <button
                    onClick={() => setShowMobileSidebar(false)}
                    className={`transition-colors ${isDarkMode ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-900"}`}
                  >
                    <X size={24} />
                  </button>
                </div>
                <MemberFilterSidebar />
              </div>
            </div>
          )}

        <div className="flex-1 flex flex-col min-w-0">
          <div
            className={`p-3 sm:p-6 border-b ${isDarkMode ? "border-gray-800 bg-[#13161B]" : "border-gray-200 bg-white"}`}
          >
            {error && (
              <div className="mb-4 bg-red-500/10 border border-red-500/50 rounded-lg p-3 sm:p-4 flex items-start gap-3">
                <AlertCircle
                  size={20}
                  className="text-red-500 mt-0.5 flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-red-500 font-semibold">Error</div>
                  <div className="text-red-400 text-sm break-words">
                    {error}
                  </div>
                </div>
                <button
                  onClick={() => setError(null)}
                  className="text-red-400 hover:text-red-300 transition-colors flex-shrink-0"
                >
                  <X size={20} />
                </button>
              </div>
            )}

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4 sm:mb-6">
              <div className="min-w-0">
                <h1
                  className={`text-2xl sm:text-3xl font-bold mb-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  Team Calendar
                </h1>
                <p
                  className={`text-sm sm:text-base font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  {selectedTeam
                    ? `${selectedTeam.name} - ${
                        showingOwnOnly
                          ? "Viewing your appointments"
                          : "Viewing team members' appointments"
                      }`
                    : "Select a team"}
                </p>
              </div>

              <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto">
                {viewMode !== "userWeek" && canViewAllMembers() && (
                  <button
                    onClick={() => setShowMobileSidebar(true)}
                    className={`lg:hidden p-2.5 border rounded-lg transition-colors ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-gray-300 hover:text-white hover:border-gray-600" : "bg-white border-gray-300 text-gray-600 hover:text-gray-900 hover:border-gray-400"}`}
                    title="Show filters"
                  >
                    <Menu size={20} />
                  </button>
                )}

                <div className="relative flex-1 sm:flex-initial">
                  <select
                    value={selectedTeam?.id || ""}
                    onChange={(e) => {
                      const team = filteredTeams.find(
                        (t) => t.id === e.target.value,
                      );
                      setSelectedTeam(team);
                    }}
                    className={`w-full sm:w-auto px-3 sm:px-4 py-2.5 border rounded-lg text-sm sm:text-base focus:border-blue-500 focus:outline-none appearance-none pr-10 font-medium ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                  >
                    {filteredTeams.map((team) => (
                      <option key={team.id} value={team.id}>
                        {team.name}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    size={16}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                  />
                </div>

                {isEngineeringTeam() &&
                  userContext?.permissions.canBookInstallations && (
                    <button
                      onClick={() => setShowBookInstall(true)}
                      className="flex items-center gap-2 px-3 sm:px-5 py-2.5 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg text-sm sm:text-base font-semibold transition-all duration-300 hover:shadow-xl hover:scale-105 flex-shrink-0"
                    >
                      <Wrench size={20} />
                      <span className="hidden sm:inline">
                        Book Installation
                      </span>
                      <span className="sm:hidden">Install</span>
                    </button>
                  )}

                {isSalesTeam() &&
                  userContext?.permissions.canBookAppointments && (
                    <button
                      onClick={() => setShowNewAppointment(true)}
                      className="flex items-center gap-2 px-3 sm:px-5 py-2.5 text-white rounded-lg text-sm sm:text-base font-semibold transition-all duration-300 hover:shadow-xl hover:scale-105 flex-shrink-0"
                      style={{
                        background:
                          "radial-gradient(50% 50% at 50% 50%, #427BCA 0%, #2F578F 100%)",
                      }}
                    >
                      <Plus size={20} />
                      <span className="hidden sm:inline">New Appointment</span>
                      <span className="sm:hidden">New</span>
                    </button>
                  )}

                {userContext?.permissions.canViewSalesCalendar ||
                userContext?.permissions.canViewEngineeringCalendar ? (
                  <button
                    onClick={() => setShowBlockTime(true)}
                    className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-orange-600 to-orange-700 text-white rounded-lg font-semibold transition-all duration-300 hover:shadow-xl hover:scale-105"
                  >
                    <Ban size={20} />
                    <span className="hidden sm:inline">Block Time</span>
                  </button>
                ) : null}

                {userContext?.permissions.canViewSalesCalendar ||
                userContext?.permissions.canViewEngineeringCalendar ? (
                  <button
                    onClick={() => setShowNearbySuggester(true)}
                    className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-indigo-600 to-blue-700 text-white rounded-lg font-semibold transition-all duration-300 hover:shadow-xl hover:scale-105"
                  >
                    <Navigation size={20} />
                    <span className="hidden sm:inline">Nearby Slots</span>
                  </button>
                ) : null}
              </div>
            </div>

            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
              <div className="flex items-center gap-2 sm:gap-3 w-full lg:w-auto">
                <button
                  onClick={() => navigateDate(-1)}
                  className={`p-2.5 border rounded-lg transition-all hover:shadow-lg flex-shrink-0 ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-gray-300 hover:text-white hover:border-gray-600" : "bg-white border-gray-300 text-gray-600 hover:text-gray-900 hover:border-gray-400"}`}
                >
                  <ChevronLeft size={20} />
                </button>

                <div
                  className={`text-base sm:text-xl font-bold flex-1 lg:min-w-[280px] xl:min-w-[320px] text-center ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  {formatDateDisplay()}
                </div>

                <button
                  onClick={() => navigateDate(1)}
                  className={`p-2.5 border rounded-lg transition-all hover:shadow-lg flex-shrink-0 ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-gray-300 hover:text-white hover:border-gray-600" : "bg-white border-gray-300 text-gray-600 hover:text-gray-900 hover:border-gray-400"}`}
                >
                  <ChevronRight size={20} />
                </button>

                <button
                  onClick={() => setSelectedDate(new Date())}
                  className={`px-3 sm:px-4 py-2.5 border rounded-lg transition-all text-xs sm:text-sm font-semibold flex-shrink-0 ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-gray-300 hover:text-white hover:border-gray-600" : "bg-white border-gray-300 text-gray-600 hover:text-gray-900 hover:border-gray-400"}`}
                >
                  Today
                </button>
              </div>

              <div
                className={`flex items-center gap-1 sm:gap-2 border rounded-lg p-1 w-full lg:w-auto overflow-x-auto ${isDarkMode ? "bg-[#0C0E12] border-gray-700" : "bg-gray-100 border-gray-200"}`}
              >
                {["day", "userWeek", "month"].map((mode) => (
                  <button
                    key={mode}
                    onClick={() => setViewMode(mode)}
                    className={`px-3 sm:px-5 py-2 rounded-lg text-xs sm:text-sm font-semibold transition-all whitespace-nowrap ${
                      viewMode === mode
                        ? "bg-blue-600 text-white shadow-lg"
                        : `${isDarkMode ? "text-gray-400 hover:text-white hover:bg-gray-800" : "text-gray-500 hover:text-gray-900 hover:bg-gray-200"}`
                    }`}
                  >
                    {mode === "userWeek"
                      ? "Week"
                      : mode.charAt(0).toUpperCase() + mode.slice(1)}
                  </button>
                ))}
              </div>

              {viewMode === "userWeek" && (
                <button
                  onClick={() =>
                    setHideWeekends((prev) => {
                      const next = !prev;
                      localStorage.setItem(
                        "calendarHideWeekends",
                        String(next),
                      );
                      return next;
                    })
                  }
                  className={`flex items-center gap-2 px-4 py-2.5 border rounded-lg transition-all font-semibold whitespace-nowrap text-xs sm:text-sm ${
                    hideWeekends
                      ? "bg-blue-600 border-blue-600 text-white"
                      : `${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-gray-300 hover:text-white hover:border-gray-600" : "bg-white border-gray-300 text-gray-600 hover:text-gray-900 hover:border-gray-400"}`
                  }`}
                  title="Toggle weekends visibility"
                >
                  <CalendarIcon size={16} />
                  <span className="hidden sm:inline">
                    {hideWeekends ? "Show Weekends" : "Hide Weekends"}
                  </span>
                  <span className="sm:hidden">Wknd</span>
                </button>
              )}

              {viewMode === "userWeek" && canViewAllMembers() && (
                <button
                  onClick={() => setShowUserSelectionModal(true)}
                  className={`flex items-center gap-2 px-4 py-2.5 border rounded-lg transition-all font-semibold whitespace-nowrap ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-gray-300 hover:text-white hover:border-gray-600" : "bg-white border-gray-300 text-gray-600 hover:text-gray-900 hover:border-gray-400"}`}
                  title="Select and order team members"
                >
                  <Users size={18} />
                  <span className="hidden sm:inline">Customize View</span>
                  <span className="sm:hidden">Users</span>
                </button>
              )}
            </div>
          </div>

          {includePrivate && (
            <div className="px-3 sm:px-6 py-2 sm:py-3 bg-blue-900/20 border-b border-blue-800/30 flex items-center gap-2 text-xs sm:text-sm text-blue-300 font-medium">
              <AlertCircle size={16} className="flex-shrink-0" />
              <span>
                Showing your private appointments. Others see them as "Busy"
                blocks.
              </span>
            </div>
          )}

          {loading ? (
            <div
              className={`flex-1 flex items-center justify-center ${isDarkMode ? "bg-[#0A0B0F]" : "bg-gray-50"}`}
            >
              <div
                className={`text-base sm:text-lg ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                Loading appointments...
              </div>
            </div>
          ) : error ? (
            <div
              className={`flex-1 flex items-center justify-center ${isDarkMode ? "bg-[#0A0B0F]" : "bg-gray-50"}`}
            >
              <div className="text-red-400 text-base sm:text-lg px-4 text-center">
                {error}
              </div>
            </div>
          ) : (
            <div className="flex-1 overflow-hidden">
              {viewMode === "day" && renderDayView()}
              {viewMode === "userWeek" && renderUserWeekView()}
              {viewMode === "month" && renderMonthView()}
            </div>
          )}
        </div>
      </div>

      {showNewAppointment && <NewAppointmentModal />}
      {showBookInstall && (
        <BookInstallModal
          onClose={() => setShowBookInstall(false)}
          onSuccess={(appointment) => {
            setShowBookInstall(false);
            fetchAppointments();
          }}
        />
      )}
      {selectedAppointment && renderAppointmentDetailsModal()}
      {showEditAppointment && editingAppointment && (
        <EditAppointmentModal
          editingAppointment={editingAppointment}
          teamMembers={teamMembers}
          userContext={userContext}
          isEngineeringTeam={isEngineeringTeam()}
          fetchAppointments={fetchAppointments}
          onClose={() => {
            setShowEditAppointment(false);
            setEditingAppointment(null);
          }}
        />
      )}
      {showBlockTime && (
        <BlockTimeModal
          onClose={() => setShowBlockTime(false)}
          onSuccess={async (result) => {
            setShowBlockTime(false);
            await fetchAppointments();
            console.log("Time blocked successfully:", result);
          }}
          teamId={selectedTeam?.id}
        />
      )}
      {showUserSelectionModal && <UserSelectionModal />}
      {showDeleteConfirm && selectedAppointment && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60]">
          <div
            className={`rounded-xl border p-6 w-full max-w-sm mx-4 shadow-2xl ${isDarkMode ? "bg-[#13151A] border-gray-700" : "bg-white border-gray-200"}`}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-500/20 rounded-lg">
                <Trash2 size={20} className="text-red-400" />
              </div>
              <h3
                className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                Delete Appointment
              </h3>
            </div>
            <p
              className={`text-sm mb-6 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
            >
              Are you sure you want to delete{" "}
              <span
                className={`font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                {selectedAppointment.title || "this appointment"}
              </span>
              ? This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className={`px-4 py-2 border rounded-lg transition-all text-sm font-medium ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-gray-300 hover:text-white hover:border-gray-600" : "bg-gray-50 border-gray-300 text-gray-600 hover:bg-gray-100"}`}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  handleDeleteAppointment(selectedAppointment.id);
                  setShowDeleteConfirm(false);
                  setShowFullTranscript(false);
                }}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-all text-sm font-medium"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      {showQualificationOverride && <QualificationOverrideModal />}
      {showNearbySuggester && (
        <NearbySlotSuggesterModal
          onClose={() => setShowNearbySuggester(false)}
          calendarType={isEngineeringTeam() ? "engineering" : "sales"}
          onSlotSelected={({
            slot,
            nearbyAppointment,
            targetAddress,
            targetPlaceId,
          }) => {
            // Pre-fill the new appointment modal with the chosen slot time and address
            setShowNearbySuggester(false);
            setShowNewAppointment(true);
            // You can extend NewAppointmentModal to accept initialData props;
            // for now we store it so it can be read by any booking flow
            console.info("[NearbySlots] Slot selected:", {
              startTime: slot.startTime,
              endTime: slot.endTime,
              location: targetAddress,
              locationPlaceId: targetPlaceId,
              nearbyAppointmentId: nearbyAppointment?.id,
            });
          }}
        />
      )}
      {showSendEmail && emailRecipient && (
        <SendEmailModal
          recipient={emailRecipient}
          onClose={() => {
            setShowSendEmail(false);
            setEmailRecipient(null);
          }}
          onSuccess={() => {
            setShowSendEmail(false);
            setEmailRecipient(null);
            // Optionally refresh appointments or show success message
          }}
        />
      )}
    </div>
  );
};

export default TeamCalendar;
